import { trackEvent } from '@/utils/analytics'
import { isAramcoEnv } from '@/config/environment'
import { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Tldraw,
  TLComponents,
  type Editor,
  track,
  getSnapshot,
  TLShape,
  DefaultContextMenu,
  DefaultContextMenuContent,
  TldrawUiMenuGroup,
  TldrawUiMenuItem,
  useEditor,
  type TLParentId,
  type TLUiContextMenuProps,
} from '@tldraw/tldraw'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'
import BoardLayout from '@/components/layout/BoardLayout'
import SplitBoardChatPanel from './SplitBoardChatPanel'
import BoardChatPanelV2 from '@/components/ChatInterface/BoardChatPanelV2'
import { useChatPanelLayout } from '@/hooks/useChatPanelLayout'
import { useUIv2 } from '@/hooks/useUIv2'
import { computeBoardFitZoom } from './utils/computeBoardFitZoom'
import { forwardWheelBehindOverlay } from './utils/forwardWheelBehindOverlay'
import { boardLayoutSignature } from './utils/boardLayoutSignature'
import { ZOOM_MIN_PERCENT, ZOOM_MAX_PERCENT_VECTOR, zoomStepLadder } from '@/constants/zoom'
import { restoreUserContent } from './utils/restoreUserContent'
import { useSidebarExpanded } from '@/hooks/useSidebarExpanded'
import { useAppStore } from '../../store'
import { useFeatureEnabled } from '@/features/featureAccess/useFeatureAccess'
import { isAnnotatorRole } from '@/types/users'
import { MODAL_TYPE } from '@/store/modalSlice'
import { persistedDocType } from '@/store/boardSlice'
import { useBugReport } from '@/components/bug-report/useBugReport'
import AiChatButton from '@/components/AiChatButton'
import {
  readRememberedConversations,
  rememberConversations,
} from './utils/rememberedConversations'
import {
  Bug,
  Folder,
  MessageSquareText,
  Share2Icon,
  FilePlus,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import BoardToolbar from './BoardToolbar'
import BoardToolbarV2 from './BoardToolbarV2'
import BoardTopBarV2 from './BoardTopBarV2'
import BoardFileSearch from './BoardFileSearch'
import BoardLeftNavRail from './BoardLeftNavRail'
import { boardOverlayInsetStartPx } from './boardLeftNavLayout'
import { toggleBoardSidebar } from './utils/sidebarToggle'
import BoardTourV2 from './BoardTourV2'
import BoardZoomControlsV2 from './BoardZoomControlsV2'
import { cleanDocumentName } from './utils/stripGraphPrefix'
import { computeBoardContentHash } from './utils/computeBoardContentHash'
import { buildHashInputsFromEditor } from './utils/buildHashInputsFromEditor'
import { computeDocumentRemoval } from './utils/computeDocumentRemoval'
import {
  collectCardRects,
  findCardAnchorForPoint,
  commentMoveUpdate,
  commentPinShapeId,
  resolvePinPlacements,
  reconcilePins,
} from './utils/commentPinScope'
import { isProgrammaticChange } from './utils/isProgrammaticChange'
import BoardEntryCardV2 from './BoardEntryCardV2'
import DocumentPreview from './DocumentPreviewShape/DocumentPreview'
import { WorkspaceOperation, DocumentPosition, apiService } from '@/service/api'
import { DocumentPreviewShapeUtil, ICON_WIDTH, ICON_HEIGHT } from './DocumentPreviewShape'
import { preloadDocumentBlobs } from './DocumentPreviewShape/PdfPreview'
import { GRID_LAYOUT, DOCS_PER_PAGE, resolveBlobDocId, sortByFilename } from './constants'
import { resolvePnidDocuments } from './pnidResolution'
import { LiveSeriesShapeUtil } from './LiveSeriesShape'
import { ConnectorCardShapeUtil, CONNECTOR_CARD_WIDTH, CONNECTOR_CARD_HEIGHT } from './ConnectorCardShape'
import { CommentPinShapeUtil } from './CommentPinShape'
import CommentModeOverlay from './CommentPinShape/CommentModeOverlay'
import ZoomModeOverlay from './ZoomModeOverlay'
import CommentComposer from './CommentPinShape/CommentComposer'
import ContextPopup from './ContextPopup'
import ShareModal from '@/components/ShareModal'
import DocumentsModal from './DocumentsModal'
import ConnectorsModal from './ConnectorsModal'
import CommentPanel from '@/components/Comments/CommentPanel'
import { shouldOpenBoardFileSearch } from './boardFileSearchShortcut'
import { watchPreviewForViewer } from './focusPreviewViewer'
import { captureBoardPreview } from './utils/captureBoardPreview'
import { boardHasLoadingCards } from './utils/boardHasLoadingCards'
import type { PnidSource } from '@/types/catalog'
import { resolveBoardDocType } from './utils/resolveBoardDocType'
import { BoardErrorBoundary } from './BoardErrorBoundary'
import { useBoardInteraction, useKeyboardShortcuts, useTableTextCopy, useBoardLeaveGuard } from './hooks'
import { enableFabricEmbedMode, isFabricEmbedMode, ALLOWED_ORIGINS, getParentOrigin } from '@/lib/fabricEmbedAuth'
import { isFeatureAllowedInEmbed } from '@/lib/embedFeatures'
import { getCurrentDirection } from '@/i18n/config'
import { useTheme } from 'next-themes'

const ContextDetails = track(ContextPopup)

/**
 * Custom tldraw context menu — injects "Search within board" always, and
 * "Add to Chat" (or "Add {N} to Chat" for multi-select) when the selection
 * contains DocPreview shapes, at the top of the right-click menu. Defined at
 * module scope so the function reference is stable across BoardCanvas renders
 * — tldraw treats a new components.ContextMenu reference as a remount signal.
 * That stability is also why the search toggle comes from the store rather
 * than a BoardCanvas prop.
 */
function BoardContextMenu(props: TLUiContextMenuProps) {
  const { t } = useTranslation()
  const editor = useEditor()
  const addDocsToChatContext = useAppStore((s) => s.addDocsToChatContext)
  const setBoardFileSearchOpen = useAppStore((s) => s.setBoardFileSearchOpen)
  const selected = editor.getSelectedShapes() as TLShape[]
  const docIds = selected
    .filter((s) => s.type === 'DocPreview')
    .map((s) => (s.props as { documentId?: string }).documentId)
    .filter((id): id is string => typeof id === 'string' && id.length > 0)
  const label = docIds.length > 1 ? `Add ${docIds.length} to Chat` : 'Add to Chat'
  return (
    <DefaultContextMenu {...props}>
      {/* FS-686: the board file search was Cmd+F-only, so nothing on screen
          advertised it. Right-click is where users already look for
          board-level actions. */}
      <TldrawUiMenuGroup id="board-search-actions">
        <TldrawUiMenuItem
          id="search-within-board"
          label={t('board:search.within_board')}
          readonlyOk
          onSelect={() => setBoardFileSearchOpen(true)}
        />
      </TldrawUiMenuGroup>
      {docIds.length > 0 && (
        <TldrawUiMenuGroup id="board-chat-context-actions">
          <TldrawUiMenuItem
            id="add-selected-to-chat"
            label={label}
            icon="link"
            readonlyOk
            onSelect={() => addDocsToChatContext(docIds)}
          />
        </TldrawUiMenuGroup>
      )}
      <DefaultContextMenuContent />
    </DefaultContextMenu>
  )
}

const NEW_BOARD_NAME = 'New Board'
const BACK_URL = '/intelligent-board'
const AUTO_PREVIEW_DELAY = 5000 // Delay in ms before auto-generating preview (5 seconds)
/**
 * UI-57: how long a comment pin must sit still before its new position is
 * saved. Long enough that a drag is one write rather than one per pointer
 * move; short enough that a release followed by an immediate reload keeps it.
 */
const PIN_MOVE_COMMIT_MS = 400
// DOCS_PER_PAGE imported from ./constants

/** Horizontal space reserved when UI v2 board chat is open — match `w-[380px]` + `right-3` (12px). */
const BOARD_V2_CHAT_RESERVE_X = 380 + 12


// Grid layout — positions icons in a dense grid per page

const getPageLocalPosition = (indexInPage: number) => ({
  x: GRID_LAYOUT.LEFT_OFFSET + (indexInPage % GRID_LAYOUT.COLUMNS) * (ICON_WIDTH + GRID_LAYOUT.MARGIN),
  y: Math.floor(indexInPage / GRID_LAYOUT.COLUMNS) * (ICON_HEIGHT + GRID_LAYOUT.MARGIN),
})

const getCardType = (type: string) => {
  switch (type) {
    case 'graph':
      return 'DocPreview'
    case 'sql':
      return 'ConnectorCard'
    case 'timeseries':
      return 'DocPreview'
    case 'device_config':
      return 'DocPreview'
    default:
      return 'DocPreview'
  }
}

const getCardDimensions = (type: string) => {
  if (getCardType(type) === 'ConnectorCard') {
    return { w: CONNECTOR_CARD_WIDTH, h: CONNECTOR_CARD_HEIGHT }
  }
  return { w: ICON_WIDTH, h: ICON_HEIGHT }
}

const Board = () => {
  const { t } = useTranslation()
  const { resolvedTheme } = useTheme()
  const { openBugReport } = useBugReport()
  const isV2 = useUIv2()
  // FS-513: board leftnav. Starts collapsed; the top-left toggle expands it in
  // place (it used to navigate away to the dashboard). Persisted across reloads.
  const [sidebarExpanded, setSidebarExpanded] = useSidebarExpanded()
  const {
    board,
    boardLocked,
    connectors,
    documents,
    renamedDocNames,
    selectedDocuments,
    showAddDocsModal,
    isSaving,
    hasPendingChanges,
    isBoardLoading,
    boardLoadError,
    setShowAddDocsModal,
    loadBoard,
    resetBoard,
    saveBoard,
    updateBoard,
    resetChatState,
    setContextId,
    removeSelectedDocuments,
    setHasPendingChanges,
    user,
    comments,
    isLoadingComments,
    loadComments,
    createComment,
    setUserHasInteracted,
    setAllowedDocumentIds,
    setFabricItemIds,
    getDocuments,
    boardFileSearchQuery,
    boardFileSearchMatchIndex,
    openModal,
  } = useAppStore()
  const [editor, setEditor] = useState<Editor | null>(null)
  const { board_id } = useParams()
  // FS-511: keep board_id in a ref so the pnid-hydration effect can tag its
  // telemetry with the CURRENT board without listing board_id as a trigger dep
  // (which would re-run the effect with the previous board's documents during
  // navigation and mis-attribute the metric).
  // UI-57: a dragged pin's position is written back once the drag settles.
  // One timer per comment, so dragging two pins in quick succession saves both.
  const pinMoveTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  useEffect(() => {
    const timers = pinMoveTimersRef.current
    // A pending save is dropped on unmount rather than fired: the editor it
    // would read the shape from is going away.
    return () => {
      for (const timer of timers.values()) clearTimeout(timer)
      timers.clear()
    }
  }, [])

  // UI-84 (Hasma): "If I open a new board, take no action in it, and then move
  // on to something else, that board is still saved as an untitled board. Over
  // time these pile up and clutter the list."
  //
  // The row is created before the board is opened, so the only place that can
  // answer "did anything happen here" is the leave. The store holds the id
  // while it is still untouched; every action that counts — a canvas or
  // document change, a rename, a comment, a query — clears it, so by the time
  // this runs a board that earned its place is no longer provisional and this
  // is a no-op.
  //
  // Deliberately keyed on the id captured at mount, not a ref: the board being
  // left is the one that was open, even if the route has already changed.
  const boardIdRef = useRef(board_id)
  boardIdRef.current = board_id
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  // Fabric embed mode: detect from URL query parameter
  const isEmbedMode = searchParams.get('embed') === 'fabric'
  // Only the V2 non-embed layout renders BoardTopBarV2, which is where the file
  // search docks. The legacy and Fabric-embed layouts have no top bar, so they
  // keep the floating panel — otherwise Cmd+F and the right-click action would
  // set the flag with nothing to render it, and Cmd+F's preventDefault would
  // swallow the browser's native find-in-page on top of that.
  const hasTopBar = isV2 && !isEmbedMode
  useEffect(() => {
    if (isEmbedMode && !isFabricEmbedMode()) {
      enableFabricEmbedMode()
    }
  }, [isEmbedMode])

  // Security: In embed mode, block any navigation away from the board route.
  // This prevents the iframe from ever showing the full IntuigenceAI application.
  useEffect(() => {
    if (!isEmbedMode) return undefined
    const unblock = () => {
      // Intercept all navigation attempts: if the target is not an embed route, block it
      const originalPushState = history.pushState.bind(history)
      const originalReplaceState = history.replaceState.bind(history)
      const isAllowedPath = (url: string | URL | null | undefined) => {
        if (!url) return true
        try {
          const pathname =
            typeof url === 'string'
              ? new URL(url, window.location.origin).pathname
              : url.pathname
          return pathname.startsWith('/embed/')
        } catch {
          return false
        }
      }
      history.pushState = function (...args: Parameters<typeof history.pushState>) {
        const url = args[2]
        if (!isAllowedPath(url as string | URL | null | undefined)) {
          console.warn('[BoardCanvas] Blocked navigation in embed mode:', url)
          return
        }
        return originalPushState(...args)
      }
      history.replaceState = function (...args: Parameters<typeof history.replaceState>) {
        const url = args[2]
        if (!isAllowedPath(url as string | URL | null | undefined)) {
          console.warn('[BoardCanvas] Blocked replaceState in embed mode:', url)
          return
        }
        return originalReplaceState(...args)
      }
      return () => {
        history.pushState = originalPushState
        history.replaceState = originalReplaceState
      }
    }
    const cleanup = unblock()
    return cleanup
  }, [isEmbedMode])

  const { initialMessage, autoSend: autoSendFromNav } = (location.state as { initialMessage?: string; autoSend?: boolean }) || {}
  // Track autoSend in local state so it can be cleared after the first send,
  // preventing re-trigger when the chat panel is minimized and reopened (unmount/remount).
  const [autoSend, setAutoSend] = useState(!!autoSendFromNav)
  // Sync when autoSendFromNav becomes true (e.g. entry card triggers navigate with autoSend)
  useEffect(() => {
    if (autoSendFromNav) {
      setAutoSend(true)
    }
  }, [autoSendFromNav])
  const [chatOpen, setChatOpen] = useState(true)
  const chatEnabled = useFeatureEnabled('chat')
  const callsEnabled = useFeatureEnabled('agent_calls')
  // Any path that reopens chat (e.g. "Add to Chat") is closed again while chat is hidden.
  useEffect(() => {
    if (!chatEnabled && chatOpen) setChatOpen(false)
  }, [chatEnabled, chatOpen])
  // "Use Entire Data Catalog" is a per-BOARD choice, so it is cleared when the
  // board is left — not when the chat panel closes.
  //
  // It used to be cleared by BoardChatPanelV2's unmount, which was right while
  // that panel was the only thing that could set it. The empty-board entry card
  // now sets it too, and it sets it BEFORE the panel exists: an unmount cleanup
  // in the panel runs during StrictMode's double-invoke at mount, so a scope
  // ticked on the entry card was wiped by the very panel opening to answer it.
  // Dev only — production has no StrictMode — which is the worst shape for it,
  // since dev is where anyone would check the button works.
  const setEntireDataCatalog = useAppStore((s) => s.setEntireDataCatalog)
  useEffect(() => {
    return () => setEntireDataCatalog(false)
  }, [setEntireDataCatalog])
  // FS-561: chat layout (size — which encodes docked-right vs corner-floating —
  // and maximized) persists via localStorage, scoped per board, so it survives
  // leaving/reopening this board without affecting other boards' chats.
  const {
    width: chatPanelWidth, setWidth: setChatPanelWidth,
    height: chatPanelHeight, setHeight: setChatPanelHeight,
    maximized: chatMaximized, setMaximized: setChatMaximized,
  } = useChatPanelLayout(board_id ?? 'board', { width: 420, height: 560 })
  const [entryDismissed, setEntryDismissed] = useState(false)

  // External requests to open the chat panel — fired by addDocToChatContext
  // / addDocsToChatContext / requestOpenChat in chatSlice. The store
  // increments a nonce; we open the panel and dismiss the entry card
  // every time it changes. Ref guard ensures the initial mount value
  // (0) doesn't trigger an open on first render.
  const chatOpenRequestNonce = useAppStore((s) => s.chatOpenRequestNonce)
  const lastChatOpenRequestRef = useRef(chatOpenRequestNonce)
  useEffect(() => {
    if (chatOpenRequestNonce === lastChatOpenRequestRef.current) return
    lastChatOpenRequestRef.current = chatOpenRequestNonce
    setChatOpen(true)
    setEntryDismissed(true)
  }, [chatOpenRequestNonce])

  // We can only decide between rendering the centered BoardEntryCardV2 and the
  // docked-right floating chat after the conversations check completes. Until
  // then, render nothing in that slot — otherwise an empty board with prior
  // chats flashes the entry card for ~500ms before snapping to the dock.
  const [conversationsChecked, setConversationsChecked] = useState(false)
  const [showTour, setShowTour] = useState(() => isV2 && !localStorage.getItem('board-tour-v2-seen'))
  const [showShareModal, setShowShareModal] = useState(false)
  const [showConnectorsModal, setShowConnectorsModal] = useState(false)
  const [showCommentPanel, setShowCommentPanel] = useState(false)
  const [previewDoc, setPreviewDoc] = useState<{ id: string; name?: string; shapeId?: string } | null>(null)
  const [isCommentMode, setIsCommentMode] = useState(false)
  const [isZoomMode, setIsZoomMode] = useState(false)
  const [isZoomOutMode, setIsZoomOutMode] = useState(false)
  const [pendingCommentPosition, setPendingCommentPosition] = useState<{
    screenX: number
    screenY: number
    canvasX: number
    canvasY: number
  } | null>(null)
  const canvasRef = useRef<HTMLDivElement>(null)
  const [chatWidth, setChatWidth] = useState(33.33) // Initial width as percentage
  const isResizingRef = useRef(false)
  // Track previous board_id to only reset chat state when switching boards
  const previousBoardIdRef = useRef<string | undefined>(undefined)
  // Pagination for canvas documents - limit how many render at once for performance
  const [canvasDocsPage, setCanvasDocsPage] = useState(0)

  // Deduplicate documents by document_id (multi-page PNIDs can cause duplicates),
  // then sort by filename (A to Z, numeric-aware) so cards have a stable,
  // predictable order across pages.
  const uniqueDocuments = useMemo(() => {
    const seen = new Set<string>()
    const deduped = (selectedDocuments as DocumentPosition[]).filter((doc: DocumentPosition) => {
      if (seen.has(doc.document_id)) return false
      seen.add(doc.document_id)
      return true
    })
    return sortByFilename<DocumentPosition>(deduped, documents, connectors, renamedDocNames)
  }, [selectedDocuments, documents, connectors, renamedDocNames])

  // Calculate visible documents for current page
  const { visibleDocuments, totalCanvasPages, hasMoreDocs } = useMemo(() => {
    const startIndex = canvasDocsPage * DOCS_PER_PAGE
    const endIndex = startIndex + DOCS_PER_PAGE
    const visible = uniqueDocuments.slice(startIndex, endIndex)
    const totalPages = Math.ceil(uniqueDocuments.length / DOCS_PER_PAGE)
    return {
      visibleDocuments: visible,
      totalCanvasPages: totalPages,
      hasMoreDocs: uniqueDocuments.length > DOCS_PER_PAGE,
    }
  }, [uniqueDocuments, canvasDocsPage])

  // FS-511 PR13: track the currently-visible doc ids in a ref so the pnid
  // hydration effect can prioritise them (fetch their sources first) WITHOUT
  // re-running on every page change. The effect still resolves the whole board,
  // so off-page name resolution never regresses.
  const visibleDocIdsRef = useRef<string[]>([])
  visibleDocIdsRef.current = visibleDocuments.map(
    (doc: DocumentPosition) => doc.document_id
  )

  // Reset page when it exceeds valid bounds due to document/board changes
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(uniqueDocuments.length / DOCS_PER_PAGE) - 1)
    if (canvasDocsPage > maxPage) {
      setCanvasDocsPage(maxPage)
    }
  }, [uniqueDocuments.length, canvasDocsPage])

  // Auto-jump the canvas to the page containing the focused search match
  // (FS-468). Without this, a match on a later page only highlights the card
  // and the user has to page through manually to find it.
  useEffect(() => {
    if (!boardFileSearchQuery) return
    const query = boardFileSearchQuery.toLowerCase()
    const nameById = new Map<string, string>()
    for (const item of [...documents, ...connectors]) {
      nameById.set(item.id, item.name.toLowerCase())
    }
    const matchPositions: number[] = []
    uniqueDocuments.forEach((doc: DocumentPosition, idx: number) => {
      const name = nameById.get(doc.document_id)
      if (name && name.includes(query)) matchPositions.push(idx)
    })
    if (matchPositions.length === 0) return
    const focusedIdx = boardFileSearchMatchIndex < matchPositions.length
      ? boardFileSearchMatchIndex
      : 0
    const targetPage = Math.floor(matchPositions[focusedIdx] / DOCS_PER_PAGE)
    if (targetPage !== canvasDocsPage) setCanvasDocsPage(targetPage)
  }, [
    boardFileSearchQuery,
    boardFileSearchMatchIndex,
    uniqueDocuments,
    documents,
    connectors,
    canvasDocsPage,
  ])

  // Prefetch blobs for the next page so pagination feels instant
  useEffect(() => {
    let cancelled = false
    const nextStart = (canvasDocsPage + 1) * DOCS_PER_PAGE
    const nextDocs = uniqueDocuments.slice(nextStart, nextStart + DOCS_PER_PAGE)
    if (nextDocs.length === 0) return
    const ids: string[] = []
    for (const doc of nextDocs) {
      const resolved = resolveBlobDocId(doc as DocumentPosition, documents)
      if (resolved) ids.push(resolved)
    }
    if (!cancelled && ids.length > 0) preloadDocumentBlobs(ids)
    return () => { cancelled = true }
  }, [canvasDocsPage, uniqueDocuments, documents])

  // Auto-open chat panel when navigating with autoSend
  useEffect(() => {
    if (autoSendFromNav && initialMessage) {
      setChatOpen(true)
      // Clear location state so auto-send doesn't re-trigger on return navigation.
      // Use native history API (not navigate()) to avoid changing location.key,
      // which would trigger ChatInterface's navigation reset and clear the pending message.
      // Preserve React Router's internal state (idx, key) to avoid corrupting history tracking.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { usr, ...routerState } = window.history.state ?? {}
      window.history.replaceState(routerState, '')
    }
  }, [autoSendFromNav, initialMessage])

  const [pnidDocuments, setPnidDocuments] = useState<
    Map<string, PnidSource>
  >(new Map())

  // =============================================================================
  // AUTO-SAVE & LEAVE WARNING STATE MANAGEMENT
  // =============================================================================
  //
  // This component implements an auto-save system with a leave warning modal.
  // The interaction between these states follows this flow:
  //
  // AUTO-SAVE FLOW:
  // 1. User makes a change → handleCanvasChange fires
  // 2. hasPendingChanges=true, hasRecentChanges=true (for 5s window)
  // 3. After 2s of inactivity → generatePreviewAutomatically runs
  // 4. Save completes → hasPendingChanges=false, hasRecentChanges=false
  // Note: hasRecentChanges is cleared immediately on successful save to prevent
  // false "unsaved changes" warnings when navigating away after save
  //
  // LEAVE WARNING FLOW:
  // 1. User clicks back while hasUnsavedChanges=true → warning modal shows
  //    (hasUnsavedChanges = hasPendingChanges || isSaving)
  // 2. Auto-save is PAUSED (isSavingBlocked=true) while modal is open
  // 3. User chooses:
  //    - "Stay" → resume auto-save, trigger immediate save if pending changes
  //    - "Leave" → discard changes, navigate away
  //
  // Note: hasRecentChanges is NOT used for the warning modal because it can
  // be re-triggered by the createNodes effect re-running after save completes,
  // causing false positives. hasPendingChanges is sufficient.
  //
  // WHY BOTH STATE AND REF VERSIONS?
  // - State (useState): Triggers re-renders, used for UI updates and dependency arrays
  // - Ref (useRef): Accessed in callbacks/closures without stale closure issues,
  //   doesn't trigger re-renders. Essential for event handlers and timeouts that
  //   need the current value without being in a dependency array.
  // =============================================================================

  // Tracks whether the "unsaved changes" warning modal is currently displayed
  // State version: triggers re-render when modal opens/closes
  // Ref version: accessed in callbacks to check current state without stale closures
  const [isLeaveWarningShown, _setIsLeaveWarningShown] = useState(false)
  const isLeaveWarningShownRef = useRef(false)

  // Timestamp of the last canvas change, used for debugging/tracking
  const lastChangeTimeRef = useRef<number | null>(null)

  // Tracks if changes were made in the last 5 seconds (prevents premature navigation)
  // State version: used in hasUnsavedChanges prop for BoardLayout
  // Ref version: accessed in callbacks after warning modal closes
  const hasRecentChangesRef = useRef(false)
  const [, setHasRecentChanges] = useState(false)

  // Ref mirror of the store's hasPendingChanges so the leave guard's blocker
  // predicate reads the freshest value, not a stale render closure.
  const hasPendingChangesRef = useRef(false)

  // Timeout refs for debouncing (stored as refs to persist across re-renders)
  const recentChangesTimeoutRef = useRef<NodeJS.Timeout | null>(null) // Clears hasRecentChanges after 5s
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null) // Triggers save after 2s of inactivity

  // When true, user chose "Leave" in warning modal - prevents any further saves
  const discardChangesRef = useRef(false)

  // When true, changes are coming from the createNodes effect, not user interaction
  // This prevents the feedback loop: save → board update → effect re-runs →
  // editor.updateShape() → handleCanvasChange → hasPendingChanges = true
  const isEffectUpdatingRef = useRef(false)
  const effectResetTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Track previous document IDs to detect when documents are added/removed via modal
  // This allows us to trigger preview generation after modal document changes
  const prevDocumentIdsRef = useRef<Set<string>>(new Set())
  const isInitialDocumentLoadRef = useRef(true) // Track if this is the first load
  const [shouldGeneratePreview, setShouldGeneratePreview] = useState(false)
  const documentChangePreviewTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Debounce setHasRecentChanges to prevent 60+ re-renders/sec during drag
  // This is safe because hasRecentChanges is only used for UI indication, not save logic
  const recentChangesDebounceRef = useRef<NodeJS.Timeout | null>(null)

  // Track if we've already zoomed to selection on initial load
  // Prevents zoom animation from running on every effect re-run (pagination, etc)
  const hasZoomedRef = useRef(false)

  // FS-562: the fit-to-content camera zoom that reads as "100%". Drives the
  // zoom readout's calibration and bounds the camera so neither the buttons nor
  // the wheel can zoom past the 200% vector cap (the old ~3000% bug).
  const [fitBaseline, setFitBaseline] = useState(1)
  // -1 sentinel (no real fit scale is negative) so the first layout always
  // enters the zoomStep-clamp block below, even for boards whose content fits
  // at pureFit === 1. Initialising to 1 made the `Math.abs(pureFit - ref) > 1e-4`
  // guard skip the clamp for those boards, leaving tldraw's default zoomSteps
  // (max z=8) so wheel/pinch could still reach ~800% (FS-562 review).
  const fitBaselineRef = useRef(-1)

  // FS-562: signature of the inputs that legitimately move the camera (page,
  // chat-panel reflow, the set of visible docs). The layout effect re-runs on
  // many incidental changes too (isSaving, selection) — re-centering on those
  // is the "jumps too early, forces repeated re-adjustment" complaint. We only
  // re-fit/re-center when this signature actually changes (or on first paint).
  const lastLayoutSigRef = useRef('')

  // FS-482: skip auto-preview uploads when the shape graph is unchanged since the
  // last save. Hash covers id/type/position/document_id of every shape on the page;
  // pure camera pans, async PDF re-renders, and master-board reposition no-ops
  // don't change it, so the cached preview is reused instead of re-uploading
  // a viewport-dependent capture.
  const lastSavedPreviewHashRef = useRef<string | null>(null)
  // Retry handle for captures deferred while card thumbnails are loading.
  const previewRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Greptile P2 on #17629: cap deferred retries so a permanently-stuck card
  // (e.g. a P&ID whose rasterisation never completes) can't poll forever.
  const previewRetryCountRef = useRef(0)
  const MAX_PREVIEW_RETRIES = 5
  // True from board open until a capture lands (or proves current). Folded
  // into the leave-guard signal so a look-only visit still flushes the
  // preview on exit — otherwise leaving before the deferred capture fires
  // keeps the stored preview blank (placeholder) forever.
  const previewOwedRef = useRef(false)

  // Track if the initial tldraw snapshot has been loaded for this board
  // Prevents stale snapshots from being reloaded on subsequent effect re-runs
  // (e.g., when DocumentsModal save returns a stale snapshot from the server)
  const hasLoadedSnapshotRef = useRef(false)

  // Track drag/interaction state using custom hook
  const isInteractingRef = useBoardInteraction(editor)

  // Handle tool change (exit comment mode)
  const handleToolChange = () => {
    setIsCommentMode(false)
    setPendingCommentPosition(null)
    setIsZoomMode(false)
    setIsZoomOutMode(false)
  }

  // Handle zoom tool activation
  const handleZoomToolClick = () => {
    setIsZoomMode((prev) => !prev)
    setIsZoomOutMode(false)
    setIsCommentMode(false)
    setPendingCommentPosition(null)
  }

  // Handle zoom-out tool activation
  const handleZoomOutToolClick = () => {
    setIsZoomOutMode((prev) => !prev)
    setIsZoomMode(false)
    setIsCommentMode(false)
    setPendingCommentPosition(null)
  }

  // Handle keyboard shortcuts using custom hook
  // Pass handleToolChange to exit comment mode when switching tools via keyboard
  useKeyboardShortcuts(editor, handleToolChange, handleZoomToolClick)

  // Allow Ctrl+C to copy selected text from data tables instead of the shape
  useTableTextCopy()

  // Board file search visibility — hidden by default, toggled via Cmd+F /
  // Ctrl+F or the "Search within board" right-click action (FS-686). Store-held
  // so the module-scope BoardContextMenu can reach the toggle too.
  const isBoardFileSearchOpen = useAppStore((s) => s.isBoardFileSearchOpen)
  const setBoardFileSearchOpen = useAppStore((s) => s.setBoardFileSearchOpen)

  // Move focus into the card preview when it opens. Every viewer scopes its own
  // Cmd+F to "the keydown happened inside me", and the board correctly stands
  // down while a preview is open (FS-657) — so with focus still on the modal,
  // NOBODY claimed the key and the browser's own find-in-page opened instead.
  // Users had to click the document first, then press Cmd+F again.
  // Waits for the viewer to mount rather than looking once: an uncached document
  // renders a loading spinner first, so a single pass finds no focus target and
  // the bug survives for the common case. Rules + tests in focusPreviewViewer.
  const previewPanelRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!previewDoc) return
    return watchPreviewForViewer(previewPanelRef.current)
  }, [previewDoc])

  // Cmd+F / Ctrl+F: open board file search (only when focus is on the board canvas, not in chat panel or text inputs)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        const shouldOpen = shouldOpenBoardFileSearch({
          isPreviewOpen: !!previewDoc,
          target: e.target as HTMLElement,
          canvas: canvasRef.current,
        })
        if (!shouldOpen) return
        e.preventDefault()
        setBoardFileSearchOpen(true)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [previewDoc, setBoardFileSearchOpen])

  // Ctrl+C / Cmd+C: copy selected document card name when exactly one DocPreview is selected
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        const target = e.target as HTMLElement
        const isTextInput =
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          (target.isContentEditable && target.getAttribute('contenteditable') === 'true')
        if (isTextInput) return
        // If user has text selected anywhere on the page, let the browser handle copy natively
        const selection = window.getSelection()
        if (selection && selection.toString().trim().length > 0) return
        if (!editor) return
        const selectedIds = editor.getSelectedShapeIds()
        if (selectedIds.length !== 1) return
        const shape = editor.getShape(selectedIds[0])
        if (!shape || shape.type !== 'DocPreview') return
        const name = (shape.props as { name?: string }).name
        if (name) {
          void navigator.clipboard.writeText(name)
          e.preventDefault()
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [editor])

  // Clean up timeout refs on component unmount to prevent memory leak
  useEffect(() => {
    return () => {
      if (recentChangesTimeoutRef.current) {
        clearTimeout(recentChangesTimeoutRef.current)
        recentChangesTimeoutRef.current = null
      }
      if (recentChangesDebounceRef.current) {
        clearTimeout(recentChangesDebounceRef.current)
        recentChangesDebounceRef.current = null
      }
    }
  }, [])

  // Memoised once — tldraw treats a new components reference as a
  // signal to remount the canvas, which would lose drag state and
  // re-bind every pointer handler. ContextDetails / BoardContextMenu
  // are stable module-level references, so [] is a valid dep array.
  const components = useMemo<TLComponents>(
    () => ({
      InFrontOfTheCanvas: ContextDetails,
      ContextMenu: BoardContextMenu,
    }),
    [],
  )

  // Stable key derived from document IDs only (ignoring positions).
  // Prevents the PNID fetch effect from re-running on auto-save when only
  // positions change but the set of documents on the board hasn't changed.
  const boardDocIdsKey = useMemo(
    () => (selectedDocuments as DocumentPosition[]).map((d: DocumentPosition) => d.document_id).sort().join(','),
    [selectedDocuments]
  )

  // Fetch PNID documents for graph documents that reference them
  useEffect(() => {
    const abortController = new AbortController()

    // Capture the board this run resolves for, at run-start, so onStats tags
    // telemetry with THIS run's board even if the route param changes before the
    // async resolution settles — correctness independent of abort-guard timing.
    const boardIdForRun = boardIdRef.current

    // Only process graph documents actually on this board, not the entire catalog
    const boardDocIds = new Set(boardDocIdsKey.split(',').filter(Boolean))

    const fetchPnidDocuments = async () => {
      // FS-511: resolve the source P&ID/PFD records for this board's graph docs
      // through the batched /v1/document/query endpoint (deduped, chunked,
      // parallelised) instead of one getDocument per graph doc. That per-doc
      // fanout saturated Chrome's per-origin connection cap on large boards and
      // left the UI stuck. See resolvePnidDocuments.
      try {
        const pnidMap = await resolvePnidDocuments(documents, boardDocIds, {
          signal: abortController.signal,
          // FS-511 PR13: resolve the visible page's sources first.
          priorityIds: new Set(visibleDocIdsRef.current),
          // FS-511 observability: report request volume so we can confirm
          // the per-doc fanout is gone (network_requests ~0) and catch regressions.
          onStats: (stats) => {
            // Don't emit telemetry for a run that was aborted (board switch):
            // it would attribute stale counts to the wrong board. Mirrors the
            // setPnidDocuments guard below.
            if (abortController.signal.aborted) return
            // Read board_id from a ref so the effect isn't a board_id dep
            // (which could re-run with the old board's documents mid-navigation).
            const currentBoardId = boardIdForRun
            console.info(
              `📍 [PNID_HYDRATION] board_id=${currentBoardId ?? 'unknown'} ` +
                `graph_docs=${stats.graphDocs} resolved_inline=${stats.resolvedInline} ` +
                `network_requests=${stats.networkRequests} resolved=${stats.resolved} ` +
                `unresolved=${stats.unresolved}`
            )
            trackEvent('board-pnid_hydrated', {
              board_id: currentBoardId,
              graph_docs: stats.graphDocs,
              resolved_inline: stats.resolvedInline,
              network_requests: stats.networkRequests,
              resolved: stats.resolved,
              unresolved: stats.unresolved,
            })
          },
        })
        if (!abortController.signal.aborted) {
          setPnidDocuments(pnidMap)
        }
      } catch (error) {
        if ((error as Error).name === 'AbortError') return
        console.debug('Failed to resolve PNID documents:', error)
      }
    }

    if (documents.length > 0) {
      fetchPnidDocuments()
    }

    return () => abortController.abort()
  }, [documents, boardDocIdsKey])

  // Load board if board_id is provided
  useEffect(() => {
    if (board_id) {
      // Reset chat state and pagination when switching to a DIFFERENT board
      // This ensures each board has its own chat context and starts at page 0
      const isNewBoard = previousBoardIdRef.current !== board_id
      if (isNewBoard) {
        resetChatState()
        setEntryDismissed(false)
        setCanvasDocsPage(0)
        // Reset document change tracking for new board
        isInitialDocumentLoadRef.current = true
        prevDocumentIdsRef.current = new Set()
        // Reset zoom flag so new board zooms to fit on initial load
        hasZoomedRef.current = false
        previewOwedRef.current = false
        // FS-573 follow-up: an in-place board switch (Recent Boards menu) keeps
        // this editor instance, so the previous board's camera zoom would leak
        // into the new board: the deferred chat-open re-fit samples the live
        // (still-animating) zoom as "the user's zoom" and lands the board at
        // e.g. 140%. Re-zero the camera exactly like a fresh mount; the fit
        // effect then opens the new board at 100%.
        editor?.setCamera({ x: 0, y: 0, z: 1 })
        // Reset snapshot loaded flag so new board loads its snapshot
        hasLoadedSnapshotRef.current = false
        // FS-482: reset preview-hash gate so the next auto-preview for this
        // board uploads once (and only once) until content actually changes.
        lastSavedPreviewHashRef.current = null
        // The file search is board-scoped but its state is global, and an
        // in-place switch never calls resetBoard() — so without this the new
        // board opens with the previous board's query still highlighting cards
        // and a match index pointing into a list that no longer exists.
        // Passing false is what clears the query and index (see the slice).
        setBoardFileSearchOpen(false)
      }
      previousBoardIdRef.current = board_id

      // Then load new board (isBoardLoading is managed by the store)
      loadBoard(board_id)
      setContextId(board_id)
      // Load comments for the board
      loadComments(board_id, { include_resolved: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board_id, resetChatState])

  // Auto-dismiss the entry card when there are existing conversations for this board.
  // Uses a direct API check keyed on board_id so stale store data from a previous
  // board can never race with the reset and re-show the entry card incorrectly.
  //
  // Also flips conversationsChecked so the entry card / docked chat slot stays
  // blank until we know which to show — prevents the entry-card → docked-chat
  // flash on boards that have no files but existing chats.
  useEffect(() => {
    if (!board_id) {
      setConversationsChecked(true)
      return
    }

    // Reported from dev: reopening a board leaves the bottom-right chat missing
    // "for some boards… it comes after sometime". The slot renders nothing at
    // all while this check is in flight — not the panel and not the collapsed
    // button — so a slow getConversationsByContext reads as the chat having
    // vanished. The gate is right to wait the FIRST time: it is what stops the
    // entry card flashing before the docked chat on a board that has prior
    // chats but no files.
    //
    // On a REOPEN we already know the answer from last time. Show the slot
    // immediately from the remembered value and let the request confirm in the
    // background; it corrects itself if the answer changed.
    const remembered = readRememberedConversations(board_id)
    if (remembered !== null) {
      if (remembered) setEntryDismissed(true)
      setConversationsChecked(true)
    } else {
      setConversationsChecked(false)
    }

    let cancelled = false
    apiService
      .getConversationsByContext(board_id)
      .then((conversations) => {
        if (cancelled) return
        const hasConversations = Boolean(conversations && conversations.length > 0)
        rememberConversations(board_id, hasConversations)
        if (hasConversations) setEntryDismissed(true)
      })
      .catch(() => {
        /* ignore — entry card will show as fallback once conversationsChecked flips */
      })
      .finally(() => {
        if (!cancelled) setConversationsChecked(true)
      })
    return () => {
      cancelled = true
    }
  }, [board_id])

  // Fabric embed mode: listen for board-specific PostMessages
  useEffect(() => {
    if (!isEmbedMode) return undefined

    function handleFabricMessage(event: MessageEvent) {
      // Security: only accept messages from verified Fabric origins
      if (!ALLOWED_ORIGINS.includes(event.origin)) return

      const message = event.data
      if (!message?.type) return

      switch (message.type) {
        case 'BOARD_SAVE_REQUEST':
          // Trigger immediate save, then notify parent using verified origin
          saveBoard(false).then(() => {
            if (window.parent && window.parent !== window) {
              window.parent.postMessage(
                { type: 'BOARD_SAVED', payload: {} },
                event.origin
              )
            }
          })
          break
        case 'BOARD_CONTEXT': {
          const ctx = message.payload as {
            allowedDocumentIds?: string[];
            fabricItemIds?: string[];
          }
          // fabricItemIds: server-side filtering by Fabric item ID (preferred)
          // Always set — empty array means no catalogs connected (show nothing)
          if (ctx.fabricItemIds !== undefined) {
            setFabricItemIds(ctx.fabricItemIds)
            // Clear allowedDocumentIds so canvas document loading isn't blocked
            // by the embed default ([]). Server-side scoping handles security.
            setAllowedDocumentIds(null)
            if (ctx.fabricItemIds.length > 0) {
              getDocuments()
            }
          }
          // allowedDocumentIds: legacy client-side filtering (fallback)
          else if (ctx.allowedDocumentIds !== undefined) {
            setAllowedDocumentIds(ctx.allowedDocumentIds)
            getDocuments({ docIds: ctx.allowedDocumentIds })
          }
          console.log('[BoardCanvas] Received BOARD_CONTEXT:', message.payload)
          break
        }
        case 'FABRIC_THEME':
          // Theme changes — can be applied if needed
          console.log('[BoardCanvas] Received FABRIC_THEME:', message.payload)
          break
        case 'OPEN_DOCUMENTS_MODAL':
          // Parent Fabric workload requested the Add/Remove Files modal
          setShowAddDocsModal()
          break
      }
    }

    window.addEventListener('message', handleFabricMessage)
    return () => window.removeEventListener('message', handleFabricMessage)
  }, [isEmbedMode, saveBoard, setAllowedDocumentIds, setFabricItemIds, getDocuments, setShowAddDocsModal])

  useEffect(() => {
    if (!editor || !board || isSaving) return

    const storeUnsubscribeRef = {
      current: undefined as (() => void) | undefined,
    }
    const selectionUnsubscribeRef = {
      current: undefined as (() => void) | undefined,
    }
    // UI-57 (claude-review): the pin-move handler's disposer. tldraw keeps
    // side-effect handlers in an array, and this effect re-runs on board,
    // isSaving, visibleDocuments and more — so discarding the return value
    // left one live handler per re-run, each arming its own debounce timer on
    // every drag. Net-correct only because the timers happen to fire in order.
    const pinMoveUnsubscribeRef = {
      current: undefined as (() => void) | undefined,
    }

    const createNodes = async () => {
      // Create document name map, using PNID filenames for graph documents
      const docNameMap: Record<string, string> = [
        ...documents,
        ...connectors,
      ].reduce(
        (
          acc: Record<string, string>,
          doc: { id: string; name: string; type?: string }
        ) => {
          // For graph documents, prefer the source PNID filename when its
          // record has loaded. Fall back to the graph row's own name. Then
          // run the shared cleanDocumentName: strips the legacy "P&ID
          // Graph "/"PFD Graph " marker and also a bare "P&ID "/"PFD "
          // prefix when the doc is typed pnid/pfd. Type passed in is the
          // *effective* type — for graphs that resolve to a source record,
          // that's the source's type, not 'graph'.
          let displayName = doc.name
          let effectiveType = doc.type
          if (doc.type === 'graph') {
            const pnidDoc = pnidDocuments.get(doc.id)
            if (pnidDoc) {
              displayName = pnidDoc.name
              effectiveType = pnidDoc.type
            }
          }
          return {
            ...acc,
            // An in-flight/just-applied rename wins over the derived name (incl.
            // the source-P&ID name used for graph cards), so the createNodes
            // rebuild can't clobber a rename the user just made.
            [doc.id]: renamedDocNames[doc.id] ?? cleanDocumentName(displayName, effectiveType),
          }
        },
        {}
      )

      // Batch-fetch names for any board documents missing from the
      // dedup-filtered document list (older duplicates, >1000 doc tenants, etc.)
      const missingIds = visibleDocuments
        .map((doc: DocumentPosition) => doc.document_id)
        .filter((id) => !docNameMap[id])

      if (missingIds.length > 0) {
        try {
          const missingDocs = await apiService.queryDocs({
            type: undefined,
            doc_ids: missingIds,
          })
          for (const doc of missingDocs) {
            // Same strip as the primary path — without it, graphs that
            // arrive via this fallback (large tenants, dedup gaps) leak
            // the legacy prefix to the card. Pass doc.type so a P&ID/PFD
            // row carrying the bare "P&ID "/"PFD " marker also cleans up.
            // A pending rename still wins so the backend refetch (which may
            // predate the rename PATCH commit) can't revert a just-renamed card.
            docNameMap[doc.id] = renamedDocNames[doc.id] ?? cleanDocumentName(doc.name, doc.type)
          }
        } catch {
          // Fallback to 'Untitled' handled downstream
        }
      }

      // Resolve the correct doc_type a card should render. Reconciles the
      // stored position type against the loaded catalog records (FS-556 custom-
      // board P&ID fast-path + graph→source-type mapping). Extracted to a pure,
      // unit-tested helper so the branch ordering is guarded by CI (Greptile P2).
      const resolveDocType = (doc: DocumentPosition): string =>
        resolveBoardDocType(doc, documents, pnidDocuments)

      // Track whether we need to load snapshot - only on initial board load
      // Uses hasLoadedSnapshotRef to prevent stale snapshots from being reloaded
      // when the effect re-runs (e.g., after DocumentsModal save returns a stale
      // server snapshot that overwrites board.tldraw_snapshot)
      const needsSnapshotLoad =
        board.tldraw_snapshot && !hasLoadedSnapshotRef.current

      // Mark that we're about to make programmatic changes (not user-initiated)
      // This prevents the feedback loop where effect changes trigger handleCanvasChange
      isEffectUpdatingRef.current = true

      editor.updateInstanceState({ isReadonly: false })
      editor.setCurrentTool('select')

      // Restore the user's hand-added content (drawings, text, arrows, images)
      // from the saved snapshot. Managed cards and pins are rebuilt at their grid
      // positions below, so they're excluded from the restore. Set the ref first
      // so a restore failure can't retry-loop across effect re-runs.
      if (needsSnapshotLoad) {
        hasLoadedSnapshotRef.current = true
        restoreUserContent(editor, board.tldraw_snapshot)
      }

      // Wait for the document list to load before creating/updating shapes so
      // that resolveDocType can correctly map graph → pnid/pfd on the first
      // render. Grid positions are applied below once documents arrive.
      if (documents.length === 0 && visibleDocuments.length > 0) {
        isEffectUpdatingRef.current = false
        return
      }

      // Collect existing shapes AFTER snapshot load so we can reuse them
      // (preserving React component state / thumbnail caches).
      const existingShapes = editor.getCurrentPageShapes()
      const existingDocumentShapes = new Map<string, TLShape>()
      existingShapes.forEach((shape) => {
        if (shape.meta?.document_id) {
          existingDocumentShapes.set(String(shape.meta.document_id), shape)
        }
      })

      const visibleDocIds = new Set(
        visibleDocuments.map((doc: DocumentPosition) => doc.document_id)
      )

      // FS-502: apply the board rebuild as a "remote" change. tldraw tags every
      // edit made inside mergeRemoteChanges with source:'remote', so the
      // source:'user' autosave listener and the source-aware shape side-effect
      // handlers below skip them. This severs the feedback loop (save → new
      // `board` ref → createNodes rebuild → autosave → save) at its source,
      // instead of relying on the best-effort 100ms isEffectUpdatingRef timer.
      editor.store.mergeRemoteChanges(() => {
        // Update existing shapes or create new ones — always at grid positions
        visibleDocuments.forEach((doc: DocumentPosition, indexInPage: number) => {
          const pos = getPageLocalPosition(indexInPage)
          const resolvedType = resolveDocType(doc)
          const { w: targetW, h: targetH } = getCardDimensions(resolvedType)
          const existingShape = existingDocumentShapes.get(doc.document_id)

          if (existingShape) {
            editor.updateShape({
              id: existingShape.id,
              type: getCardType(resolvedType),
              x: pos.x,
              y: pos.y,
              props: {
                name: docNameMap[doc.document_id] || 'Untitled',
                documentId: doc.document_id,
                doc_type: doc.doc_type,
                w: targetW,
                h: targetH,
              },
              meta: {
                locked: false,
                document_id: doc.document_id,
                resolved_doc_type: resolvedType,
              },
            })
          } else {
            editor.createShape({
              type: getCardType(resolvedType),
              x: pos.x,
              y: pos.y,
              props: {
                name: docNameMap[doc.document_id] || 'Untitled',
                documentId: doc.document_id,
                doc_type: doc.doc_type,
                collapsed: true,
                w: targetW,
                h: targetH,
              },
              meta: {
                locked: false,
                document_id: doc.document_id,
                resolved_doc_type: resolvedType,
              },
            })
          }
        })

        // Remove shapes not on the current page (off-page or deleted docs)
        existingDocumentShapes.forEach((shape, docId) => {
          if (!visibleDocIds.has(docId)) {
            editor.deleteShape(shape.id)
          }
        })
      })

      if (boardLocked) {
        editor.updateInstanceState({ isReadonly: true })
        editor.setCurrentTool('select')
      } else {
        editor.sideEffects.registerAfterChangeHandler('shape', (prev, next, source) => {
          // FS-502: skip programmatic rebuild edits. createNodes wraps them in
          // mergeRemoteChanges so they arrive as source:'remote'; the legacy
          // isEffectUpdatingRef gate is kept as a backstop for any unwrapped path.
          if (isProgrammaticChange(source, isEffectUpdatingRef.current)) return
          if (!next?.meta?.document_id) return

          // Detect double-click preview request
          if (
            (next.meta as Record<string, unknown>)?.previewRequested &&
            (next.meta as Record<string, unknown>).previewRequested !== (prev.meta as Record<string, unknown>)?.previewRequested
          ) {
            const shapeProps = next.props as Record<string, unknown>
            setPreviewDoc({
              id:
                typeof shapeProps.documentId === 'string' && shapeProps.documentId
                  ? shapeProps.documentId
                  : String(next.meta.document_id),
              name: typeof shapeProps.name === 'string' ? shapeProps.name : undefined,
              shapeId: next.id,
            })
          }

          if (prev.x !== next.x || prev.y !== next.y) {
            // TODO: Fix Types
            /* eslint-disable  @typescript-eslint/no-explicit-any */
            const shapeProps: any = next.props

            const operations = {
              upsert: [
                {
                  document_id: String(next.meta.document_id),
                  x: next.x,
                  y: next.y,
                  // live_stream → 'timeseries' at every persist boundary
                  // (see persistedDocType) — shape props keep the raw type.
                  doc_type: persistedDocType({ doc_type: shapeProps.doc_type }),
                },
              ],
              remove: [],
            }

            updateBoard(operations as WorkspaceOperation)
          }
        })

        // UI-57: a dragged comment pin has to STAY where it was dropped. Pins
        // are rebuilt from the comment records on every load (resolvePinPlacements),
        // so a move that is not written back is undone by the next reconcile —
        // which would read as the drag silently failing rather than not existing.
        //
        // Where it is written back depends on what the pin hangs off. A board
        // comment lives at page coordinates in position_x/position_y. A file
        // comment is parented to its card and stored as rel_x/rel_y in metadata,
        // so that it travels WITH the card; writing page coordinates for one of
        // those would detach it the moment the card moved.
        pinMoveUnsubscribeRef.current = editor.sideEffects.registerAfterChangeHandler('shape', (prev, next, source) => {
          if (isProgrammaticChange(source, isEffectUpdatingRef.current)) return
          if (next.type !== 'CommentPin') return
          if (prev.x === next.x && prev.y === next.y) return

          const commentId = (next.props as { commentId?: string }).commentId
          if (!commentId) return

          // Persist ONCE, when the drag settles — not on every intermediate
          // position. tldraw emits a change per pointer move, and writing each
          // one through updateComment put the store (and therefore the pin-sync
          // effect below, which is keyed on `comments`) into a loop WHILE the
          // drag was still running. That effect does store.remove() +
          // createShape(): it was tearing down and rebuilding the very shape
          // under the pointer mid-gesture, which is what produced a second pin
          // that could not be opened. Reported against the first build of this
          // change; the drag itself was working, the write-back was the fault.
          const timers = pinMoveTimersRef.current
          const queued = timers.get(commentId)
          if (queued) clearTimeout(queued)

          timers.set(
            commentId,
            setTimeout(() => {
              timers.delete(commentId)
              // Re-read the shape rather than closing over `next`: the settled
              // position is the one to save, not the position that happened to
              // start the timer.
              const shape = editor.getShape(next.id)
              if (!shape) return

              // Read the store at call time, not at registration: this handler
              // is installed once per editor and would otherwise hold the
              // comment list and the updater from first mount forever.
              const store = useAppStore.getState()

              // Which fields carry the position depends on what the pin hangs
              // off; commentMoveUpdate owns that choice and is unit-tested.
              const boardId = boardIdRef.current
              const existing = (boardId ? store.comments[boardId] : undefined)?.find(
                (c) => c.id === commentId
              )?.metadata as Record<string, unknown> | undefined

              // Not fire-and-forget: a refused move would otherwise revert on
              // the next reconcile with nothing said. Pins the viewer does not
              // own are locked above, so reaching this is unexpected.
              void store
                .updateComment(
                  commentId,
                  commentMoveUpdate(shape, editor.getCurrentPageId(), existing)
                )
                .catch((error: unknown) => {
                  console.error('[BoardCanvas] Failed to save comment position:', error)
                  toast.error(
                    t('board:comments.move_failed', {
                      defaultValue: 'Could not save the comment position',
                    })
                  )
                })
            }, PIN_MOVE_COMMIT_MS)
          )
        })

        // Bring selected shapes to front when selection changes
        selectionUnsubscribeRef.current = editor.sideEffects.registerAfterChangeHandler(
          'instance_page_state',
          (prev, next) => {
            // Don't bring to front during programmatic updates (e.g., loading board, auto-save)
            // This preserves the z-index/stacking order from the saved snapshot
            if (isEffectUpdatingRef.current) return

            const prevSelected = prev?.selectedShapeIds ?? []
            const nextSelected = next?.selectedShapeIds ?? []

            // Find newly selected shapes
            const newlySelected = nextSelected.filter(
              (id) => !prevSelected.includes(id)
            )

            if (newlySelected.length > 0) {
              editor.bringToFront(newlySelected)
            }
          }
        )

        // React to actual shape deletes instead of inferring deletion from
        // a visibleDocuments / currentShapes mismatch. The inference fired
        // false positives on store events whose closure didn't perfectly
        // match the canvas state (e.g. while a preview modal was open on a
        // multi-page board) and silently removed an unrelated doc.
        // registerAfterDeleteHandler gives us only the signal we want: the
        // shape that was actually deleted.
        const pendingDeleteIds = new Set<string>()
        let deleteFlushTimer: ReturnType<typeof setTimeout> | null = null

        const flushDeletes = () => {
          deleteFlushTimer = null
          if (pendingDeleteIds.size === 0) return

          const docIds = Array.from(pendingDeleteIds)
          pendingDeleteIds.clear()

          // Read the freshest selectedDocuments — between this delete and the
          // closure capture, an auto-save may have synced new positions in.
          const currentSelected = useAppStore.getState().selectedDocuments as DocumentPosition[]
          const removal = computeDocumentRemoval(docIds, currentSelected)
          if (!removal) return

          removeSelectedDocuments(removal.removedIds)
          updateBoard(removal.operations)
          if (!isLeaveWarningShownRef.current) {
            saveBoard(false).then((success) => {
              if (success) {
                if (recentChangesTimeoutRef.current) {
                  clearTimeout(recentChangesTimeoutRef.current)
                  recentChangesTimeoutRef.current = null
                }
                hasRecentChangesRef.current = false
                setHasRecentChanges(false)
              }
            })
          }
        }

        const handleShapeDelete = (deletedShape: TLShape, source: 'remote' | 'user') => {
          // FS-502: off-page removals during the rebuild arrive as source:'remote'
          // (createNodes wraps them in mergeRemoteChanges), so they're no longer
          // mistaken for a user delete and persisted. isEffectUpdatingRef stays
          // as a backstop.
          if (isProgrammaticChange(source, isEffectUpdatingRef.current)) return
          const documentId = deletedShape.meta?.document_id
          if (!documentId) return
          pendingDeleteIds.add(String(documentId))
          if (deleteFlushTimer) clearTimeout(deleteFlushTimer)
          // Batch deletes from a single user action (e.g. multi-select Delete)
          // into one removeSelectedDocuments + saveBoard call.
          deleteFlushTimer = setTimeout(flushDeletes, 0)
        }

        const disposeDeleteHandler = editor.sideEffects.registerAfterDeleteHandler(
          'shape',
          handleShapeDelete,
        )
        storeUnsubscribeRef.current = () => {
          // Flush synchronously before tearing down. If the effect re-runs
          // (e.g. a page change) in the same frame as a shape-delete, the
          // queued setTimeout(0) would be cancelled and the removal lost.
          // flushDeletes no-ops when nothing is pending.
          if (deleteFlushTimer) clearTimeout(deleteFlushTimer)
          flushDeletes()
          disposeDeleteHandler()
        }
      }

      // Initial open: snap to 100% centered on content. Page changes after
      // that: fit content into the visible band (capped at 100%).
      // Computes bounds manually from shape geometries to avoid selectAll/selectNone
      // which would trigger the bringToFront selection handler and corrupt z-order.
      if (visibleDocuments.length > 0) {
        requestAnimationFrame(() => {
          if (!editor) return
          const shapes = editor.getCurrentPageShapes().filter(
            (s: TLShape) => s.meta?.document_id
          )
          if (shapes.length === 0) return
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
          for (const shape of shapes) {
            const bounds = editor.getShapePageBounds(shape.id)
            if (!bounds) continue
            minX = Math.min(minX, bounds.x)
            minY = Math.min(minY, bounds.y)
            maxX = Math.max(maxX, bounds.x + bounds.w)
            maxY = Math.max(maxY, bounds.y + bounds.h)
          }
          if (!isFinite(minX)) return

          // FS-562: skip the re-fit/re-center unless a layout-relevant input
          // changed. Without this the camera jumped whenever the effect re-ran
          // for an autosave or a card selection, yanking the user's view.
          // Only a chat panel that actually reserves layout width (V2, non-embed)
          // should invalidate the signature — otherwise toggling chat on V1 /
          // embed boards triggers a superfluous re-center (see boardLayoutSignature).
          const chatAffectsLayout = isV2 && !isEmbedMode && chatOpen
          const layoutSig = boardLayoutSignature({
            canvasDocsPage,
            chatAffectsLayout,
            visibleDocumentIds: visibleDocuments.map((d) => d.document_id),
          })
          if (hasZoomedRef.current && layoutSig === lastLayoutSigRef.current) return
          lastLayoutSigRef.current = layoutSig

          const contentBounds = { x: minX, y: minY, w: maxX - minX, h: maxY - minY }
          const viewportBounds = editor.getViewportScreenBounds()
          const inset = Math.min(128, viewportBounds.width * 0.28)
          const chatRightReserve = chatAffectsLayout ? BOARD_V2_CHAT_RESERVE_X : 0
          // Fit / center in the band: left of toolbar offset, right of floating chat (v2)
          const availableWidth = Math.max(
            200,
            viewportBounds.width - GRID_LAYOUT.LEFT_OFFSET - chatRightReserve,
          )
          // First paint fits-to-content but never zooms past 100%; page changes
          // fit without undoing a user's zoom-in. See computeBoardFitZoom — this
          // replaced an unconditional `zoom = 1` first-paint that let large
          // drawings / a narrow viewport (chat open) overflow ("too zoomed").
          const zoom = computeBoardFitZoom({
            firstPaint: !hasZoomedRef.current,
            contentWidth: contentBounds.w,
            contentHeight: contentBounds.h,
            availableWidth,
            viewportHeight: viewportBounds.height,
            inset,
            currentZoom: editor.getZoomLevel(),
          })

          // FS-562: calibrate "100%" to the pure fit-to-content zoom (ignoring
          // any user zoom-in) and bound the camera's zoomSteps to [25%, 200%] of
          // it (linear ratio), so the readout reads exactly 25%/200% at the
          // clamps and the wheel can't reach the old ~3000% readout.
          // IMPORTANT: set the options BEFORE the animated setCamera below —
          // calling setCameraOptions *after* setCamera cancels the in-flight fit
          // animation, leaving the camera at its pre-fit zoom (the "opens at
          // 122%" bug).
          const pureFit = computeBoardFitZoom({
            firstPaint: true,
            contentWidth: contentBounds.w,
            contentHeight: contentBounds.h,
            availableWidth,
            viewportHeight: viewportBounds.height,
            inset,
            currentZoom: editor.getZoomLevel(),
          })
          if (Math.abs(pureFit - fitBaselineRef.current) > 1e-4) {
            fitBaselineRef.current = pureFit
            setFitBaseline(pureFit)
            editor.setCameraOptions({
              ...editor.getCameraOptions(),
              // UI-28: the full ladder, not just [min, fit, max]. tldraw's own
              // zoom in/out walks this array, so three entries made the
              // keyboard and pinch jump 25% -> 100% -> 200% while the +/-
              // buttons stepped normally. Ends are unchanged, so the readout
              // still reads exactly 25% and 200% at the clamps (FS-562).
              zoomSteps: zoomStepLadder(pureFit, ZOOM_MIN_PERCENT, ZOOM_MAX_PERCENT_VECTOR),
            })
          }

          // Center content in the available area (right of toolbar)
          const cameraX =
            -contentBounds.x +
            (GRID_LAYOUT.LEFT_OFFSET + (availableWidth - contentBounds.w * zoom) / 2) / zoom
          const cameraY =
            -contentBounds.y +
            (viewportBounds.height - contentBounds.h * zoom) / 2 / zoom
          // First paint lands INSTANTLY: an animated first fit leaves a 500ms
          // window where follow-up re-fits (documents arriving in waves, the
          // chat auto-opening) sample the in-flight zoom as "the user's zoom"
          // or cancel the animation via setCameraOptions and strand the camera
          // between values — the board then opens at 104–140% instead of 100%.
          // With the landing instant, later re-fits always sample a settled
          // camera and converge on the fit baseline.
          editor.setCamera(
            { x: cameraX, y: cameraY, z: zoom },
            hasZoomedRef.current ? { animation: { duration: 300 } } : undefined,
          )
          hasZoomedRef.current = true
        })
      } else if (Math.abs(1 - fitBaselineRef.current) > 1e-4) {
        // Empty board: no fit runs and the camera idles at the mount default
        // z=1 — recalibrate the readout baseline to match. A baseline left
        // over from a previously-viewed board made an empty board land at
        // ~147% after an in-place switch.
        fitBaselineRef.current = 1
        setFitBaseline(1)
      }
    }

    createNodes().finally(() => {
      // Reset the flag after a short delay to allow any queued store events to process
      // This ensures handleCanvasChange ignores all programmatic changes from this effect
      // Using finally() ensures this runs even if createNodes throws an error
      effectResetTimeoutRef.current = setTimeout(() => {
        isEffectUpdatingRef.current = false
        effectResetTimeoutRef.current = null
      }, 100)
    })

    return () => {
      // Clear the timeout to prevent race condition when effect re-runs quickly
      // Without this, the old timeout could reset the flag while new effect is running
      if (effectResetTimeoutRef.current) {
        clearTimeout(effectResetTimeoutRef.current)
        effectResetTimeoutRef.current = null
      }
      if (storeUnsubscribeRef.current) {
        storeUnsubscribeRef.current()
      }
      if (selectionUnsubscribeRef.current) {
        selectionUnsubscribeRef.current()
      }
      if (pinMoveUnsubscribeRef.current) {
        pinMoveUnsubscribeRef.current()
        pinMoveUnsubscribeRef.current = undefined
      }
    }
  }, [
    editor,
    board,
    visibleDocuments,
    canvasDocsPage,
    boardLocked,
    isSaving,
    documents,
    connectors,
    updateBoard,
    saveBoard,
    removeSelectedDocuments,
    pnidDocuments,
    renamedDocNames,
    selectedDocuments,
    isV2,
    isEmbedMode,
    chatOpen,
  ])

  // Track user interaction - only show save toasts after user has actually interacted with the canvas
  useEffect(() => {
    if (!editor || !canvasRef.current) return

    const handleUserInteraction = () => {
      setUserHasInteracted(true)
    }

    // Use the canvas container ref which is more reliable than querying for .tl-container
    const container = canvasRef.current
    container.addEventListener('pointerdown', handleUserInteraction, {
      once: true,
    })

    return () => {
      // Note: The listener may have already been removed by { once: true }, but this is safe
      container.removeEventListener('pointerdown', handleUserInteraction)
    }
  }, [editor, setUserHasInteracted])

  const handleChatOpen = () => {
    // When closing the chat, clear autoSend so reopening doesn't re-trigger
    // the auto-send of the initial message (ChatInterface unmounts on close).
    if (chatOpen) {
      setAutoSend(false)
    }
    setChatOpen(!chatOpen)
  }

  const handleMouseDown = () => {
    isResizingRef.current = true
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizingRef.current) return

      const containerWidth = window.innerWidth
      const newChatWidth = ((containerWidth - e.clientX) / containerWidth) * 100

      // Constrain chat width between 25% and 60% (increased minimum for better UX)
      if (newChatWidth >= 25 && newChatWidth <= 60) {
        setChatWidth(newChatWidth)
      }
    }

    const handleMouseUp = () => {
      isResizingRef.current = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [])

  // A page change (or doc add/remove/reorder) changes which card shapes exist;
  // key the pin sync on the visible document ids (a stable string) so it
  // re-runs when the on-screen cards change, but not on the metadata-only
  // refreshes that churn visibleDocuments's array reference.
  const visibleDocIds = visibleDocuments
    .map((doc: DocumentPosition) => doc.document_id)
    .join('|')

  // Sync comment pins. Each pin parents to its file's card so it travels with
  // the file; a file's pins exist only while its card is on the page, and
  // board-level comments parent to the page. Re-runs on page change so pins
  // re-attach to the freshly-rebuilt cards.
  useEffect(() => {
    if (!editor || !board_id) return

    // Skip sync while comments are loading to prevent a race condition where
    // all pins are incorrectly removed as orphans before the API response arrives
    if (isLoadingComments) return

    const boardComments = comments[board_id] || []
    const commentById = new Map(boardComments.map((c) => [c.id, c]))

    // Resolve where every pin should live (parented under its file's card, or
    // free on the page) from the cards currently rendered on this page.
    const cardShapeIdByDocId = new Map(
      collectCardRects(editor).map((r) => [r.documentId, r.shapeId])
    )
    const placements = resolvePinPlacements(
      boardComments,
      cardShapeIdByDocId,
      editor.getCurrentPageId()
    )
    const placementByCommentId = new Map(placements.map((p) => [p.commentId, p]))

    const existingPins = editor
      .getCurrentPageShapes()
      .filter((shape) => shape.type === 'CommentPin')

    // Decide which pins to drop (deleted, off-page, or stale parent) and which
    // placements still need a pin — see reconcilePins for the rules.
    const { removePinIds, createCommentIds } = reconcilePins(
      existingPins.map((pin) => ({
        id: pin.id,
        commentId: (pin.props as any).commentId,
        parentId: pin.parentId,
      })),
      placements
    )
    const removeSet = new Set(removePinIds)

    // Apply as remote changes so page-driven pin churn neither dirties the
    // board nor trips the source:'user' shape side-effect handlers.
    editor.store.mergeRemoteChanges(() => {
      // store.remove() bypasses canDelete() (false for CommentPins). Map back
      // to the real shapes so the ids stay typed as TLShapeId.
      const pinIdsToRemove = existingPins
        .filter((pin) => removeSet.has(pin.id))
        .map((pin) => pin.id)
      if (pinIdsToRemove.length > 0) {
        editor.store.remove(pinIdsToRemove)
      }

      // Ownership decides isLocked, and `user` can resolve AFTER this effect
      // first runs — on that pass every comment fails the ownership test and
      // every pin is created locked, including the viewer's own. Creating them
      // is not enough; the lock has to be re-checked on later passes too, or a
      // slow-loading session leaves the board permanently unmovable.
      for (const pin of existingPins) {
        if (removeSet.has(pin.id)) continue
        const comment = commentById.get((pin.props as { commentId?: string }).commentId ?? '')
        if (!comment) continue
        const shouldLock = comment.user_id !== user?.userId
        if (pin.isLocked !== shouldLock) {
          editor.updateShape({ id: pin.id, type: 'CommentPin', isLocked: shouldLock })
        }
      }

      for (const commentId of createCommentIds) {
        const placement = placementByCommentId.get(commentId)
        const comment = commentById.get(commentId)
        if (!placement || !comment) continue
        // UI-57: derive the shape id from the comment id. Two creators race
        // here — this effect, and handleCommentSubmit right after the comment
        // POSTs — and with auto-generated ids neither could see the other's
        // pin, so a fresh comment got TWO, stacked exactly. A deterministic id
        // makes that impossible: the second creator finds the first.
        const pinId = commentPinShapeId(comment.id)
        if (editor.getShape(pinId)) continue
        editor.createShape({
          id: pinId,
          type: 'CommentPin',
          parentId: placement.parentId as TLParentId,
          x: placement.x,
          y: placement.y,
          // UI-57 (Greptile): PUT /v1/comments/:id is owner-only
          // (comments.service.ts throws "You can only update your own
          // comments"), so a move made by anyone else cannot be saved. Left
          // draggable, it would slide under the cursor, 403 quietly, and snap
          // back on the next reconcile — the worst of both. Locking the pin
          // says so up front. The HTMLContainer keeps pointerEvents, so a
          // locked pin still opens its thread; only the drag is refused.
          isLocked: comment.user_id !== user?.userId,
          props: {
            w: 40,
            h: 40,
            commentId: comment.id,
            resolved: comment.is_resolved,
            hasReplies: comment.reply_count > 0,
            unreadCount: 0, // TODO: Calculate based on user's last read
            authorName: comment.user_id,
          },
        })
      }
    })
    // `user?.userId` is a real dependency, not lint noise: it decides which
    // pins are locked, and it can arrive after the first run.
  }, [editor, board_id, comments, isLoadingComments, visibleDocIds, user?.userId])

  const handleCanvasMount = (e: Editor) => {
    // Every canvas mount starts the camera at z=1 (below) — and the canvas
    // REMOUNTS during an in-place board switch (the Recent Boards menu): the
    // board-loading gate swaps Tldraw for a spinner and back. The fit refs
    // live in BoardCanvas and survive that remount, so post-remount fit runs
    // skipped as "layout signature unchanged", stranding the camera at z=1
    // over the new board's smaller fit baseline — every landing read a
    // constant ~140%. Restart the refs with the camera: the first fit after
    // any mount is a first paint again, landing instantly at 100%.
    hasZoomedRef.current = false
    lastLayoutSigRef.current = ''
    e.updateInstanceState({ isGridMode: true })
    e.setCamera({ x: 0, y: 0, z: 1 })
    // Read theme synchronously from localStorage to avoid race with React hydration
    const storedTheme = localStorage.getItem('intuigence-theme') || 'light'
    const isDark = storedTheme === 'dark' ||
      (storedTheme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
    e.user.updateUserPreferences({ colorScheme: isDark ? 'dark' : 'light' })

    // Disable tldraw's default URL→bookmark behavior. Dropping or pasting a URL
    // onto the canvas (e.g. dragging a card's "View Diagram" edit link, whose
    // href is /graph-editor/:id?boardName=…) was auto-creating stray bookmark
    // "link preview" cards that looked like a second editor opening on the
    // board. No-op the url handler so URLs never become bookmark shapes.
    e.registerExternalContentHandler('url', () => {})

    setEditor(e)
  }

  // Sync tldraw dark mode with application theme
  useEffect(() => {
    if (editor) {
      editor.user.updateUserPreferences({ colorScheme: resolvedTheme === 'dark' ? 'dark' : 'light' })
    }
  }, [editor, resolvedTheme])

  // Handle comment tool activation
  const handleCommentToolClick = () => {
    if (!isCommentMode) {
      setIsCommentMode(true)
      toast.info(t('board:comments.click_to_place'))
    } else {
      setIsCommentMode(false)
      setPendingCommentPosition(null)
      toast.info(t('board:comments.mode_cancelled'))
    }
  }

  // Handle canvas position click (show composer)
  const handlePositionClick = (
    screenX: number,
    screenY: number,
    canvasX: number,
    canvasY: number
  ) => {
    setPendingCommentPosition({ screenX, screenY, canvasX, canvasY })
  }

  // Handle composer submit (create comment + pin)
  const handleComposerSubmit = async (
    content: string,
    mentionedUserIds?: string[]
  ) => {
    if (!pendingCommentPosition || !board_id || !editor) return

    try {
      // Anchor the comment to the file card under the click (parent its pin to
      // that card so it travels with the file); empty background = board-level.
      const point = {
        x: pendingCommentPosition.canvasX,
        y: pendingCommentPosition.canvasY,
      }
      const anchor = findCardAnchorForPoint(point, collectCardRects(editor))

      // Create comment in backend with position (+ file anchor when on a card)
      const newComment = await createComment(board_id, {
        content,
        position_x: pendingCommentPosition.canvasX,
        position_y: pendingCommentPosition.canvasY,
        mentioned_user_ids: mentionedUserIds,
        ...(anchor
          ? {
              metadata: {
                document_id: anchor.documentId,
                rel_x: anchor.rel_x,
                rel_y: anchor.rel_y,
              },
            }
          : {}),
      })

      // Create the comment pin shape — parented to the card (card-relative
      // coords) for a file comment, or free on the page for a board comment.
      // Same deterministic id as the sync effect above — whichever of the two
      // runs first wins, and the other is a no-op instead of a second pin.
      const pinId = commentPinShapeId(newComment.id)
      if (!editor.getShape(pinId)) editor.createShape({
        id: pinId,
        type: 'CommentPin',
        ...(anchor ? { parentId: anchor.shapeId as TLParentId } : {}),
        x: anchor ? anchor.rel_x : point.x,
        y: anchor ? anchor.rel_y : point.y,
        props: {
          w: 40,
          h: 40,
          commentId: newComment.id,
          resolved: false,
          hasReplies: false,
          unreadCount: 0,
          authorName: user?.userId || 'Unknown',
        },
      })

      if (board_id) {
        trackEvent('board-comment_pinned', { board_id })
      }

      // Clear states
      setPendingCommentPosition(null)
      setIsCommentMode(false)
      toast.success(t('board:comments.created'))
    } catch (error) {
      console.error('Failed to create comment:', error)
      toast.error(t('board:comments.failed_to_create'))
    }
  }

  // Handle composer cancel
  const handleComposerCancel = () => {
    setPendingCommentPosition(null)
  }

  // Automatic preview generation. Self-reference for the deferred retry goes
  // through a ref (the callback identity can't reference itself directly).
  const generatePreviewRef = useRef<() => Promise<void>>()
  const generatePreviewAutomatically = useCallback(async () => {
    // Annotators are read-only outside the P&ID editor: never fire the
    // preview-save (PUT /v1/workspaces/:id) — it 403s and pops a spurious
    // read-only toast on open of any board that lacks a preview image.
    if (isAnnotatorRole(user?.role)) return
    // Don't save if leave warning is shown or user chose to discard changes
    if (
      !editor ||
      !canvasRef.current ||
      isSaving ||
      isLeaveWarningShownRef.current ||
      discardChangesRef.current
    ) {
      return
    }

    // Cards still showing their thumbnail-loading skeleton would freeze as
    // blank-white tiles in the capture — and the content hash below covers
    // only the shape graph/geometry, so the blank capture would gate every
    // retry off and the board would keep "rendering nothing" on the boards
    // list until a card moves. Defer and retry until the pixels are real.
    if (boardHasLoadingCards(canvasRef.current)) {
      // Give up after the cap (without pinning the hash): the next user edit
      // or board open starts a fresh attempt once content can actually paint.
      if (previewRetryCountRef.current >= MAX_PREVIEW_RETRIES) return
      previewRetryCountRef.current += 1
      if (previewRetryTimeoutRef.current) clearTimeout(previewRetryTimeoutRef.current)
      previewRetryTimeoutRef.current = setTimeout(() => {
        previewRetryTimeoutRef.current = null
        void generatePreviewRef.current?.()
      }, 2000)
      return
    }
    previewRetryCountRef.current = 0

    // FS-482: build a hash of the on-page shape graph. If it matches the last
    // saved preview, skip the upload — the captured pixels would only differ
    // because of viewport/camera state, not anything the user would expect to
    // change. This kills the "preview drifts every couple minutes" symptom
    // that fired whenever async PDF loads / master-board reposition / camera
    // animations leaked store events past the 100ms isEffectUpdatingRef gate.
    // Geometry (w/h/rotation) is part of the key so resizes and rotations
    // re-fire the upload — see utils/computeBoardContentHash for the spec.
    const shapesForHash = editor.getCurrentPageShapes()
    const contentHash = computeBoardContentHash(
      buildHashInputsFromEditor(editor, shapesForHash),
    )
    // null signals "at least one shape's bounds aren't resolved yet" —
    // refuse to capture (we'd render a half-laid-out board) AND refuse to
    // pin a sentinel hash. The next debounce after bounds settle will see
    // a real hash, mismatch the stored one, and proceed.
    if (contentHash === null) {
      return
    }
    if (contentHash === lastSavedPreviewHashRef.current) {
      previewOwedRef.current = false
      return
    }

    try {
      // FS-482: render the preview off-DOM via tldraw's SVG export. Each
      // custom shape util implements `toSvg` (see shapeNodeToSvgImage), so
      // the export gets the same pixels the user sees on-canvas without
      // touching the live editor's camera, selection, or viewport. There
      // is no setCamera/rAF/restore dance and therefore no flicker.
      const dataUrl = await captureBoardPreview(editor, shapesForHash)
      if (!dataUrl) {
        // No content to capture (empty board or all bounds unresolved).
        // Skip the upload — the hash gate above already ensured we don't
        // pin a sentinel, so the next debounce after layout settles will
        // try again.
        return
      }

      // Get current snapshot
      const { document, session } = getSnapshot(editor.store)

      // Get current document positions
      const customDocShapes: DocumentPosition[] = []
      const shapes = editor.getCurrentPageShapes()
      shapes.forEach((shape: TLShape) => {
        const props = shape.props as any
        if (shape.meta.document_id) {
          customDocShapes.push({
            document_id: String(shape.meta.document_id),
            x: shape.x,
            y: shape.y,
            doc_type: persistedDocType({ doc_type: props.doc_type }),
          })
        }
      })

      const operations: WorkspaceOperation = {
        upsert: customDocShapes,
        remove: [],
        tldraw_snapshot: {
          document,
          session,
        },
      }

      // Update board with preview image and save
      updateBoard(operations, dataUrl)
      const success = await saveBoard()

      // Only clear recent changes flag after successful save to prevent
      // false "unsaved changes" warning when user navigates away
      if (success) {
        previewOwedRef.current = false
        lastSavedPreviewHashRef.current = contentHash
        if (recentChangesTimeoutRef.current) {
          clearTimeout(recentChangesTimeoutRef.current)
          recentChangesTimeoutRef.current = null
        }
        hasRecentChangesRef.current = false
        setHasRecentChanges(false)
      }
    } catch (error) {
      console.error('Error auto-generating preview:', error)
      // Silent failure - don't show error toast for auto-preview
    }
  }, [editor, isSaving, updateBoard, saveBoard, user])
  generatePreviewRef.current = generatePreviewAutomatically

  // Cancel any deferred preview retry on unmount.
  useEffect(() => {
    return () => {
      if (previewRetryTimeoutRef.current) clearTimeout(previewRetryTimeoutRef.current)
    }
  }, [])

  // Refresh the stored preview once per board OPEN (not only on edits).
  // Boards latched with a blank preview (captured pre-fix, while thumbnails
  // were still loading) otherwise stay blank until the user happens to move
  // a card — the auto-preview only ran on user-originated store changes.
  // generatePreviewAutomatically itself waits for card thumbnails (the
  // defer/retry above) and is hash-gated within the session, so this is one
  // clean capture per visit once pixels are real.
  const openCaptureBoardRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!editor || !board || visibleDocuments.length === 0) return
    if (openCaptureBoardRef.current === board_id) return
    openCaptureBoardRef.current = board_id
    previewOwedRef.current = true
    // Give the initial shape creation a beat; the defer/retry loop handles
    // thumbnails that take longer.
    const id = setTimeout(() => {
      void generatePreviewRef.current?.()
    }, 1500)
    return () => clearTimeout(id)
  }, [editor, board, board_id, visibleDocuments.length])

  // Keep hasPendingChangesRef in sync with the store flag for the leave guard.
  useEffect(() => {
    hasPendingChangesRef.current = hasPendingChanges
  }, [hasPendingChanges])

  // Flush the pending autosave before navigating away from the board (see
  // useBoardLeaveGuard). Reuses generatePreviewAutomatically (hash-gated, so a
  // no-op when nothing changed); awaited because the snapshot is captured
  // partway through that function.
  const flushPendingSave = useCallback(async () => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current)
      autoSaveTimeoutRef.current = null
    }
    await generatePreviewAutomatically()
  }, [generatePreviewAutomatically])

  // Save the board on any in-app navigation away from it, so a stroke drawn
  // within the autosave debounce window isn't lost. Disabled in embed mode
  // (iframed, no in-app router navigation to guard).
  useBoardLeaveGuard({
    enabled: !!board_id && !isEmbedMode,
    getHasUnsavedChanges: () =>
      hasPendingChangesRef.current ||
      hasRecentChangesRef.current ||
      previewOwedRef.current,
    flush: flushPendingSave,
  })

  // Detect when documents are added/removed and trigger preview generation
  // Uses a ref for the timeout to prevent it from being cleared when selectedDocuments
  // reference changes (e.g., during position sync after save)
  useEffect(() => {
    const currentDocIds = new Set(
      (selectedDocuments as DocumentPosition[]).map((doc: DocumentPosition) => doc.document_id)
    )
    const prevDocIds = prevDocumentIdsRef.current

    // Check if document IDs actually changed (not just positions)
    const documentsAdded = [...currentDocIds].some((id) => !prevDocIds.has(id))
    const documentsRemoved = [...prevDocIds].some((id) => !currentDocIds.has(id))
    const documentsChanged = documentsAdded || documentsRemoved

    // Handle initial load - record the IDs without triggering preview,
    // UNLESS this is a new board with no existing preview (documents were just added)
    if (isInitialDocumentLoadRef.current) {
      if (currentDocIds.size > 0 || prevDocIds.size > 0) {
        isInitialDocumentLoadRef.current = false
        prevDocumentIdsRef.current = currentDocIds

        // If the board has no preview yet and documents were just added,
        // trigger preview generation so the board card shows a thumbnail
        if (!board?.preview_image && currentDocIds.size > 0 && prevDocIds.size === 0) {
          if (documentChangePreviewTimeoutRef.current) {
            clearTimeout(documentChangePreviewTimeoutRef.current)
          }
          documentChangePreviewTimeoutRef.current = setTimeout(() => {
            documentChangePreviewTimeoutRef.current = null
            setShouldGeneratePreview(true)
          }, 300)
        }
      }
      return
    }

    // Only update ref and trigger preview if IDs actually changed
    if (documentsChanged) {
      prevDocumentIdsRef.current = currentDocIds

      // Clear any pending timeout
      if (documentChangePreviewTimeoutRef.current) {
        clearTimeout(documentChangePreviewTimeoutRef.current)
      }

      // Trigger preview generation after canvas renders
      documentChangePreviewTimeoutRef.current = setTimeout(() => {
        documentChangePreviewTimeoutRef.current = null
        setShouldGeneratePreview(true)
      }, 300)
    }
  }, [selectedDocuments, board?.preview_image])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (documentChangePreviewTimeoutRef.current) {
        clearTimeout(documentChangePreviewTimeoutRef.current)
      }
    }
  }, [])

  // Trigger preview generation when documents are added/removed via modal
  // Wait for isSaving to be false before generating preview
  useEffect(() => {
    if (shouldGeneratePreview && !isSaving) {
      setShouldGeneratePreview(false)
      generatePreviewAutomatically()
    }
  }, [shouldGeneratePreview, isSaving, generatePreviewAutomatically])

  // Auto-generate preview after changes (debounced)
  useEffect(() => {
    // Don't set up listener when leave warning is shown
    if (!editor || !board || isSaving || isLeaveWarningShown) return

    const handleCanvasChange = () => {
      // Ignore changes from the createNodes effect (programmatic updates, not user-initiated)
      // This prevents the feedback loop: save → board update → effect re-runs →
      // editor.updateShape() → handleCanvasChange → hasPendingChanges = true
      if (isEffectUpdatingRef.current) {
        return
      }

      // Track recent changes via ref immediately (no re-render)
      lastChangeTimeRef.current = Date.now()
      hasRecentChangesRef.current = true

      // Use ref synced with tldraw's reactive system (reliable during drag)
      // Reading editor.inputs directly here is UNRELIABLE - the store listener fires
      // AFTER shape changes but editor.inputs may already be stale/reset
      if (isInteractingRef.current) {
        // During interaction: only set pending flag via debounce (fires once after 100ms)
        if (!recentChangesDebounceRef.current) {
          recentChangesDebounceRef.current = setTimeout(() => {
            setHasPendingChanges(true)
            setHasRecentChanges(true)
            recentChangesDebounceRef.current = null
          }, 100)
        }
      } else {
        // Not interacting: update state immediately
        setHasPendingChanges(true)
        setHasRecentChanges(true)
        if (recentChangesDebounceRef.current) {
          clearTimeout(recentChangesDebounceRef.current)
          recentChangesDebounceRef.current = null
        }
      }
      // Notify Fabric parent of state change (embed mode only)
      if (isEmbedMode && window.parent && window.parent !== window) {
        const origin = getParentOrigin()
        if (origin) {
          window.parent.postMessage({ type: 'BOARD_STATE_CHANGED', payload: {} }, origin)
        }
      }

      // Clear the recent changes flag after 5 seconds of inactivity
      // Use a ref to persist the timeout across effect cleanups
      if (recentChangesTimeoutRef.current) {
        clearTimeout(recentChangesTimeoutRef.current)
      }
      recentChangesTimeoutRef.current = setTimeout(() => {
        hasRecentChangesRef.current = false
        setHasRecentChanges(false)
        recentChangesTimeoutRef.current = null
      }, 5000)

      // Clear existing timeout
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
        autoSaveTimeoutRef.current = null
      }

      // Don't schedule save if leave warning is shown or user discarded changes
      if (isLeaveWarningShownRef.current || discardChangesRef.current) return

      // Set new timeout to generate preview after delay
      autoSaveTimeoutRef.current = setTimeout(() => {
        autoSaveTimeoutRef.current = null
        generatePreviewAutomatically()
      }, AUTO_PREVIEW_DELAY)
    }

    // Listen to store changes
    // FS-502: react only to user-originated edits. The board rebuild after a
    // save is applied via mergeRemoteChanges (source:'remote'), so it no longer
    // schedules an autosave — which is what broke the runaway save loop.
    const unsubscribe = editor.store.listen(handleCanvasChange, {
      scope: 'document',
      source: 'user',
    })

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
        autoSaveTimeoutRef.current = null
      }
      // Don't clear recentChangesTimeoutRef here - let it persist
      unsubscribe()
    }
  }, [
    editor,
    board,
    isSaving,
    isLeaveWarningShown,
    generatePreviewAutomatically,
    setHasPendingChanges,
    isEmbedMode,
    isInteractingRef,
  ])

  // Auto-trigger preview regeneration for boards with corrupted previews
  // from a specific incident in March 2025
  useEffect(() => {
    if (!board || !editor) return

    const createdAt = new Date(board.created_at)
    const updatedAt = new Date(board.updated_at)
    const corruptedStart = new Date('2025-03-08T00:00:00Z')
    const corruptedEnd = new Date('2025-04-01T00:00:00Z')
    const isFromAffectedPeriod = createdAt >= corruptedStart && createdAt < corruptedEnd

    const timeDiffSeconds = (updatedAt.getTime() - createdAt.getTime()) / 1000
    const hasOriginalPreview = timeDiffSeconds < 60

    if (isFromAffectedPeriod && hasOriginalPreview) {
      console.log(
        `Board ${board.id} has old preview - will regenerate after canvas loads...`
      )

      // Wait for canvas to fully load (5 seconds), then trigger preview regeneration
      const timeoutId = setTimeout(() => {
        console.log(`Generating fresh preview for board ${board.id}`)
        generatePreviewAutomatically()
        // Note: If generation fails, hasOriginalPreview stays true and will retry on next render
        // If generation succeeds, updated_at changes and hasOriginalPreview becomes false
      }, 5000)

      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [board, editor, generatePreviewAutomatically])

  const boardActions = [
    {
      onClick: openBugReport,
      element: (
        <Bug className="w-6 h-6 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer" />
      ),
    },
    {
      onClick: () => setShowCommentPanel(true),
      element: (
        <MessageSquareText className="w-6 h-6 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer" />
      ),
    },
    {
      onClick: () => {},
      element: (
        <div className="relative group cursor-not-allowed">
          <Button variant="outline" disabled className="pointer-events-none">
            <Share2Icon className="w-6 h-6" />
            {t('common:buttons.share')}
          </Button>
          {!isAramcoEnv && (
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-30">
              {t('common:general.coming_soon')}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900"></div>
            </div>
          )}
        </div>
      ),
    },
    {
      onClick: () => {},
      element: (
        <div className="relative group cursor-not-allowed">
          <Button variant="outline" disabled className="pointer-events-none">
            <svg width="16" height="16" viewBox="0 0 22 12" fill="none" className="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg">
              <path d="M6.5 1H6C3.23858 1 1 3.23858 1 6C1 8.76142 3.23858 11 6 11H8C10.7614 11 13 8.76142 13 6M15.5 11H16C18.7614 11 21 8.76143 21 6C21 3.23858 18.7614 1 16 1H14C11.2386 1 9 3.23858 9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {t('board:actions.add_connectors')}
          </Button>
          {!isAramcoEnv && (
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-30">
              {t('common:general.coming_soon')}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900"></div>
            </div>
          )}
        </div>
      ),
    },
    {
      onClick: () => navigate('/data-catalog'),
      element: (
        <Button variant="outline">
          <Folder className="w-4 h-4 mr-2" />
          {t('nav:menu.data_catalog')}
        </Button>
      ),
    },
    {
      onClick: () => setShowAddDocsModal(),
      element: (
        <Button variant="outline">
          <FilePlus className="w-4 h-4 mr-2" />
          {t('board:actions.add_documents')}
        </Button>
      ),
    },
  ]

  const myCustomUtils = [
    DocumentPreviewShapeUtil,
    ConnectorCardShapeUtil,
    LiveSeriesShapeUtil,
    CommentPinShapeUtil,
  ]

  // Show loading if:
  // 1. Explicitly loading (isBoardLoading is true), OR
  // 2. Initial mount state (board_id exists, board is null, and no error yet)
  if (isBoardLoading || (board_id && !board && !boardLoadError)) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    )
  }

  // Show error if load failed or no board data
  if (!board) {
    // In embed mode: show a minimal error and notify the parent — never show navigation
    if (isEmbedMode) {
      // Signal the Fabric parent that the board failed to load
      const errorOrigin = getParentOrigin()
      if (window.parent && window.parent !== window && errorOrigin) {
        window.parent.postMessage(
          { type: 'APP_ERROR', payload: { code: 'BOARD_NOT_FOUND', boardId: board_id } },
          errorOrigin
        )
      }
      return (
        <div className="flex h-full w-full items-center justify-center bg-gray-50 dark:bg-dark-muted">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-dark-text mb-2">
              Board not available
            </h2>
            <p className="text-gray-600 dark:text-gray-300">
              This board could not be loaded. Please close and recreate the item.
            </p>
          </div>
        </div>
      )
    }

    return (
      <div className="flex h-full w-full items-center justify-center bg-gray-50 dark:bg-dark-muted">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-dark-text mb-2">
            {t('board:errors.unknown_board')}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {t('board:errors.unknown_board_description')}
          </p>
          <Button
            variant="outline"
            onClick={() => navigate(BACK_URL)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('board:errors.go_to_boards')}
          </Button>
        </div>
      </div>
    )
  }

  // Shared canvas internals — Tldraw + overlays + modals
  const canvasInternals = (
    <>
      {/* Board file search, for the layouts with no top bar to dock into. */}
      {!hasTopBar && isBoardFileSearchOpen && (
        <div
          className={
            getCurrentDirection() === 'rtl'
              ? 'absolute top-4 right-4 z-30'
              : 'absolute top-4 left-4 z-30'
          }
        >
          <BoardFileSearch onClose={() => setBoardFileSearchOpen(false)} />
        </div>
      )}
      <BoardErrorBoundary>
        <Tldraw
          components={components}
          onMount={handleCanvasMount}
          hideUi
          shapeUtils={myCustomUtils}
        >
        {isFeatureAllowedInEmbed('boardToolbar') && (
          isV2 ? (
            <>
              <BoardToolbarV2
                isLocked={boardLocked}
                onCommentClick={handleCommentToolClick}
                onToolChange={handleToolChange}
                onAddClick={() => setShowAddDocsModal()}
              />
              <BoardZoomControlsV2 fitBaseline={fitBaseline} />
            </>
          ) : (
            <BoardToolbar
              isLocked={boardLocked}
              isZoomMode={isZoomMode}
              isZoomOutMode={isZoomOutMode}
              onCommentClick={handleCommentToolClick}
              onZoomClick={handleZoomToolClick}
              onZoomOutClick={handleZoomOutToolClick}
              onToolChange={handleToolChange}
            />
          )
        )}
        {isFeatureAllowedInEmbed('commentMode') && (
          <CommentModeOverlay
            isActive={isCommentMode && !pendingCommentPosition}
            editor={editor}
            onPositionClick={handlePositionClick}
            onExitMode={() => setIsCommentMode(false)}
          />
        )}
        <ZoomModeOverlay
          isActive={isZoomMode || isZoomOutMode}
          isZoomOut={isZoomOutMode}
          editor={editor}
          onExitMode={() => { setIsZoomMode(false); setIsZoomOutMode(false) }}
        />
        {isFeatureAllowedInEmbed('commentMode') && pendingCommentPosition && (
          <CommentComposer
            x={pendingCommentPosition.screenX}
            y={pendingCommentPosition.screenY}
            onSubmit={handleComposerSubmit}
            onCancel={handleComposerCancel}
          />
        )}
        {/* Preview panel — must stay inside Tldraw for useEditor() context */}
        {previewDoc && (
          <div
            className="fixed inset-y-0 end-0 z-[9999] flex items-center justify-center bg-black/40"
            // FS-580: symmetric padding so the panel centers on the window like
            // Chrome's viewer. It used to pad right by the chat width when the
            // chat was open, which pushed the panel left of center and left a
            // wide dead zone beside it on large screens; the panel is a modal
            // above a dimmed backdrop (z-9999), so it may overlay the chat.
            //
            // The backdrop stops at the leftnav rail instead of running the full
            // width. Opening a card used to dim and disable the whole rail — the
            // bug-report button, the nav, the account menu — because the backdrop
            // covered it AND closes the preview on click, so reaching for any of
            // them dismissed the card rather than doing anything. A preview is not
            // an app-modal dialog; it has no reason to take the chrome hostage.
            // `insetInlineStart` rather than `left` because the rail sits at
            // `start-0` and flips under RTL.
            style={{
              insetInlineStart: boardOverlayInsetStartPx(hasTopBar, sidebarExpanded),
              ...(isV2 ? { padding: 32 } : {}),
            }}
            onClick={() => setPreviewDoc(null)}
            onPointerDown={(e) => e.stopPropagation()}
            // FS-569: the backdrop swallows wheel events, freezing the chat
            // behind it. Forward wheels that land on the dim itself (not ones
            // bubbling from the panel) to the scrollable under the cursor —
            // the chat scrolls; the canvas (no overflow scroller) stays inert.
            onWheel={(e) => {
              // Keep wheels from bubbling on to tldraw's own listeners (the
              // backdrop lives inside the Tldraw tree), matching the
              // onPointerDown guard above.
              e.stopPropagation()
              if (e.target !== e.currentTarget) return
              forwardWheelBehindOverlay(e.currentTarget, e.clientX, e.clientY, e.deltaY, e.deltaMode)
            }}
          >
            <div
              ref={previewPanelRef}
              className={isV2
                ? "bg-white dark:bg-dark-card rounded-xl shadow-2xl flex flex-col overflow-hidden w-full max-w-4xl h-[80vh] relative"
                : "bg-white dark:bg-dark-card rounded-xl shadow-2xl flex flex-col overflow-hidden relative w-full h-full"
              }
              style={!isV2 ? { margin: 16 } : undefined}
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPreviewDoc(null)}
                className="absolute top-2 right-2 z-10"
              >
                <X className="w-4 h-4" />
              </Button>
              <BoardErrorBoundary>
                <div className="flex-1 min-h-0 overflow-hidden">
                  <DocumentPreview
                    documentId={previewDoc.id}
                    name={previewDoc.name}
                    shapeId={previewDoc.shapeId}
                    showFooter
                    onRemoveFromBoard={() => setPreviewDoc(null)}
                    width={undefined}
                    height={undefined}
                  />
                </div>
              </BoardErrorBoundary>
            </div>
          </div>
        )}
      </Tldraw>
      </BoardErrorBoundary>
      {/* Modals rendered outside Tldraw to avoid event interception */}
      <DocumentsModal
        isOpen={showAddDocsModal}
        onClose={() => setShowAddDocsModal(false)}
        onReopen={() => setShowAddDocsModal(true)}
      />
      {isFeatureAllowedInEmbed('connectorsModal') && (
        <ConnectorsModal
          isOpen={showConnectorsModal}
          onClose={() => setShowConnectorsModal(false)}
        />
      )}
      {isFeatureAllowedInEmbed('shareModal') && (
        <ShareModal
          isOpen={showShareModal}
          setShow={setShowShareModal}
          {...(board_id
            ? {
                target: {
                  type: 'workspace' as const,
                  id: board_id,
                  name: board?.name || NEW_BOARD_NAME,
                },
              }
            : {})}
        />
      )}
      {/* Pagination controls - only show when there are more docs than can fit on one page */}
      {hasMoreDocs && (
        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-white/95 dark:bg-dark-card/95 backdrop-blur-sm shadow-lg rounded-xl px-4 py-2 flex items-center gap-3 z-30 border border-gray-200 dark:border-dark-border">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCanvasDocsPage((p) => Math.max(0, p - 1))}
            disabled={canvasDocsPage === 0}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-gray-600 dark:text-gray-300 min-w-[120px] text-center">
            {t('board:showing_docs', {
              start: canvasDocsPage * DOCS_PER_PAGE + 1,
              end: Math.min(
                (canvasDocsPage + 1) * DOCS_PER_PAGE,
                uniqueDocuments.length
              ),
              total: uniqueDocuments.length,
              defaultValue: `${
                canvasDocsPage * DOCS_PER_PAGE + 1
              }-${Math.min(
                (canvasDocsPage + 1) * DOCS_PER_PAGE,
                uniqueDocuments.length
              )} of ${uniqueDocuments.length}`,
            })}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              setCanvasDocsPage((p) =>
                Math.min(totalCanvasPages - 1, p + 1)
              )
            }
            disabled={canvasDocsPage >= totalCanvasPages - 1}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </>
  )

  // ── V2 layout: full-width canvas + floating chat overlay ──
  if (hasTopBar) {
    return (
      <div className="board-workspace-v2 h-full w-full relative flex bg-[#f4f5f7] dark:bg-[hsl(240,5%,10%)]">
        {/* FS-513: leftnav rail (push layout). Occupies 80px when expanded so
            the canvas and its controls reflow to the right and stay fully
            visible; collapses back to 0 width for the original full-bleed view.
            Board-scoped — other V2 screens mount AppSidebar permanently. */}
        <BoardLeftNavRail open={sidebarExpanded} />

        {/* Board area — shifts right when the rail opens. Floating overlays
            (top bar, entry card, chat panel) anchor to this box rather than the
            workspace, so they sit beside the rail instead of under it. */}
        <div className="relative flex-1 min-w-0 h-full">
        {/* Full-width canvas */}
        <div ref={canvasRef} className="h-full w-full relative">
          <BoardTopBarV2
            boardName={board?.name || NEW_BOARD_NAME}
            boardId={board_id}
            // FS-663: the share dialog's only entry point. Requires a saved
            // board — there is nothing to grant access to before it has an id.
            {...(board_id ? { onShare: () => setShowShareModal(true) } : {})}
            onRename={(newName) => {
              if (board_id && newName !== board?.name) {
                useAppStore.getState().renameBoard(board_id, newName)
              }
            }}
            // FS-513: single toggle — expand/collapse the leftnav in place
            // (icon flips with `sidebarExpanded`). Was `navigate(BACK_URL)`,
            // which exited the board. "Back to boards" is now the Boards nav
            // item once the rail is open. Delegates to the extracted, unit-
            // tested handler (flips state + emits telemetry, never navigates).
            onSidebarToggle={() =>
              toggleBoardSidebar({
                expanded: sidebarExpanded,
                setExpanded: setSidebarExpanded,
                boardId: board_id,
              })
            }
            sidebarExpanded={sidebarExpanded}
            onCallAiEngineer={callsEnabled ? () => openModal(MODAL_TYPE.callConfig) : undefined}
            chatOpen={chatOpen && (entryDismissed || uniqueDocuments.length > 0 || !!initialMessage)}
            chatPanelWidth={chatPanelWidth}
            chatPanelHeight={chatPanelHeight}
          />
          {canvasInternals}
        </div>

        {/* Board entry card — shown when board is empty and not dismissed.
            Suppressed until the conversations check completes so an empty
            board with prior chats doesn't flash the centered entry card
            before snapping to the docked-right chat. */}
        {conversationsChecked && chatEnabled && uniqueDocuments.length === 0 && !entryDismissed && !initialMessage && (
          <BoardEntryCardV2
            onSendMessage={(message, agentId) => {
              setEntryDismissed(true)
              setChatOpen(true)
              // Set agent + input in store so chat panel picks it up
              const store = useAppStore.getState()
              store.setActiveAgent(agentId)
              store.setInputValue(message)
              // Navigate with initial message to trigger auto-send
              navigate(location.pathname, {
                state: { initialMessage: message, autoSend: true },
                replace: true,
              })
            }}
          />
        )}

        {/* Comment panel */}
        {isFeatureAllowedInEmbed('commentMode') && board_id && (
          <CommentPanel
            workspaceId={board_id}
            isOpen={showCommentPanel}
            onClose={() => setShowCommentPanel(false)}
          />
        )}

        {/* Floating chat panel — resizable, with maximize support.
             When resized near full-height, snaps to fill the right side (matching Figma).
             Gated on conversationsChecked alongside the entry card so the two
             never render together (would flash both during the initial load
             of a board with no files but prior chats). */}
        {conversationsChecked && chatEnabled && chatOpen && (entryDismissed || uniqueDocuments.length > 0 || initialMessage) ? (
          <div
            className={
              chatMaximized
                ? 'absolute inset-0 z-20'
                : chatPanelHeight >= (typeof window !== 'undefined' ? window.innerHeight : 900) - 100
                  ? 'absolute top-0 bottom-0 right-0 z-20'
                  : 'absolute bottom-3 right-3 z-20'
            }
            style={chatMaximized ? undefined : chatPanelHeight >= (typeof window !== 'undefined' ? window.innerHeight : 900) - 100
              ? {
                  width: `${chatPanelWidth}px`,
                }
              : {
                  width: `${chatPanelWidth}px`,
                  height: `${chatPanelHeight}px`,
                  maxWidth: 'calc(100% - 1.5rem)',
                  maxHeight: 'calc(100% - 1.5rem)',
                }
            }
          >
            <BoardChatPanelV2
              onClose={handleChatOpen}
              autoSendMessage={autoSend}
              navigationKey={location.key}
              initialMessage={initialMessage}
              isMaximized={chatMaximized}
              isDockedRight={chatPanelHeight >= (typeof window !== 'undefined' ? window.innerHeight : 900) - 100}
              onToggleMaximize={() => setChatMaximized((v) => !v)}
              width={chatPanelWidth}
              onWidthChange={setChatPanelWidth}
              height={chatPanelHeight}
              onHeightChange={setChatPanelHeight}
            />
          </div>
        ) : conversationsChecked && chatEnabled && (entryDismissed || uniqueDocuments.length > 0 || initialMessage) && !chatOpen ? (
          <button
            type="button"
            onClick={handleChatOpen}
            className="absolute bottom-4 right-4 z-20 w-12 h-12 rounded-full bg-[var(--chat-fab-bg)] hover:bg-[var(--chat-fab-bg-hover)] text-white shadow-lg flex items-center justify-center transition-colors"
            aria-label={t('board:actions.open_chat', { defaultValue: 'Open chat' })}
          >
            <MessageSquareText className="w-5 h-5" />
          </button>
        ) : null}

        {/* Document preview is rendered inside Tldraw (canvasInternals) for useEditor() context */}

        {/* First-time tour */}
        <BoardTourV2
          isOpen={showTour}
          onClose={() => {
            setShowTour(false)
            localStorage.setItem('board-tour-v2-seen', '1')
          }}
        />
        </div>
      </div>
    )
  }

  // ── Legacy layout: side-by-side split with resize divider ──
  const boardContent = (
    <>
      <div
        ref={canvasRef}
        style={{ width: chatOpen ? `${100 - chatWidth}%` : '100%' }}
        className="h-full transition-[width] duration-300 relative"
      >
        {canvasInternals}
        {!chatOpen && chatEnabled && (
          <AiChatButton isOpen={chatOpen} onClick={handleChatOpen} />
        )}
      </div>
      {isFeatureAllowedInEmbed('commentMode') && board_id && (
        <CommentPanel
          workspaceId={board_id}
          isOpen={showCommentPanel}
          onClose={() => setShowCommentPanel(false)}
        />
      )}
      {chatOpen && (
        <div
          onMouseDown={handleMouseDown}
          className="w-1 h-full cursor-col-resize bg-gray-300 dark:bg-gray-600 hover:bg-orange-500 dark:hover:bg-orange-500 transition-colors flex-shrink-0"
        />
      )}
      <div
        style={{ width: chatOpen ? `${chatWidth}%` : '0%' }}
        className="h-full overflow-hidden transition-[width] duration-300"
      >
        {chatEnabled && (
          <SplitBoardChatPanel
            embedded={isEmbedMode}
            onClose={handleChatOpen}
            autoSendMessage={autoSend}
            navigationKey={location.key}
            initialMessage={initialMessage}
          />
        )}
      </div>
    </>
  )

  // In embed mode, render without BoardLayout header
  if (isEmbedMode) {
    return (
      <div className="h-full w-full flex">
        {boardContent}
      </div>
    )
  }

  return (
    <BoardLayout
      name={board?.name || NEW_BOARD_NAME}
      backUrl={BACK_URL}
      actions={boardActions}
      handleBackCallback={resetBoard}
      hasUnsavedChanges={hasPendingChanges || isSaving}
      boardId={board_id}
    >
      {boardContent}
    </BoardLayout>
  )
}

export default Board
